import React, { useState, useEffect, useCallback, useRef } from 'react';
import { devLog, devWarn } from '../utils/devLog';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import ApiStatus from '../components/ApiStatus';
import FileUploader, { UploadedFile } from '../components/FileUploader';
import Dashboard from '../components/Dashboard';
import { useProductTour } from '../contexts/ProductTourContext';
import { useDocuments, PolicyDocument } from '../contexts/DocumentContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { aiService } from '../services/aiService';
// Policy-specific prompts are now imported dynamically in functions
import { defaultPolicyPrompts } from '../prompts/policyPrompts';
import { chatContextService, ChatContext } from '../services/chatContextService';
import PdfViewer, { CitationHighlight } from '../components/PdfViewer';
import FeatureCard from '../components/FeatureCard';
import PdfDocumentViewer from '../components/PdfDocumentViewer';
import HtmlPdfViewer from '../components/HtmlPdfViewer';
import ClientCard from '../components/ClientCard';
import EditCustomerModal from '../components/EditCustomerModal';
import DeleteCustomerModal from '../components/DeleteCustomerModal';
import WorkflowTools from '../components/WorkflowTools';
import ProjectManagement from '../components/ProjectManagement';
import { Project } from '../components/ProjectCard';
import { projectService } from '../services/projectService';
import GroupManagementModal from '../components/GroupManagementModal';
import ShareCustomerDialog from '../components/ShareCustomerDialog';
import CustomerAuditModal from '../components/CustomerAuditModal';
import ProjectActivityModal from '../components/ProjectActivityModal';
import TransferCustomerDialog from '../components/TransferCustomerDialog';
import SharedCustomerBadge from '../components/SharedCustomerBadge';
import DocumentIntelligenceChip from '../components/DocumentIntelligenceChip';
import AMS360ImportModal from '../components/AMS360ImportModal';
import { ams360Service } from '../services/ams360Service';
import CustomerSelectionView, {
  Customer,
  ComparisonReport,
  CustomerSortOrder,
  CustomerFilters,
  DEFAULT_CUSTOMER_FILTERS,
} from '../components/CustomerSelectionView';
import ProjectDetailView from '../components/ProjectDetailView';
import NewCustomerModal from '../components/NewCustomerModal';
import CustomerChatDrawer from '../components/CustomerChatDrawer';

// Local chat types (UI removed but backend logic maintained)
export interface ChatMessage {
  id: string;
  content: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  isStreaming?: boolean;
  citations?: any[];
}

export interface CitationClick {
  type: 'coordinate' | 'quote';
  documentId: string;
  documentName?: string;
  page?: number;
  startOffset?: number;
  endOffset?: number;
  text?: string;
  found?: boolean;
}

export interface QuoteCitation {
  id: string;
  type: 'quote';
  text: string;
  displayText: string;
  fullMatch: string;
  startIndex: number;
  endIndex: number;
}

// Legacy citation service removed
interface ParsedCitation {
  id?: string;
  quote?: string;
  text?: string;
  documentId: string;
  docId?: string;
  page?: number;
  startOffset?: number;
  endOffset?: number;
  documentName?: string;
  context?: string;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// Customer and ComparisonReport types imported from CustomerSelectionView

// Display name helper: personal lines show primary contact name, commercial shows company
export const getCustomerDisplayName = (c: { name: string; company?: string; lineType?: string }) =>
  c.lineType === 'personal' ? c.name : (c.company || c.name);

interface UnderwritingReport {
  id: string;
  userId: string;
  customerId: string;
  title: string;
  content: string;
  createdAt: Date;
  updatedAt: Date;
  projectId?: string;
}

const Home: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { customerId: urlCustomerId, projectId: urlProjectId } = useParams<{ customerId?: string; projectId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [showUploader, setShowUploader] = useState(false);
  const { startTour } = useProductTour();
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  
  // Customer/Prospect/Suspects state
  const [selectedView, setSelectedView] = useState<'all' | 'customers' | 'prospects' | 'suspects'>('all');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [prospects, setProspects] = useState<Customer[]>([]);
  const [suspects, setSuspects] = useState<Customer[]>([]);
  const [comparisonReports, setComparisonReports] = useState<ComparisonReport[]>([]);
  const [projectReports, setProjectReports] = useState<ComparisonReport[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedReport, setSelectedReport] = useState<ComparisonReport | null>(null);
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [newCustomerType, setNewCustomerType] = useState<'customer' | 'prospect' | 'suspect'>('customer');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState<CustomerSortOrder>('default');
  const [customerFilters, setCustomerFilters] = useState<CustomerFilters>(DEFAULT_CUSTOMER_FILTERS);

  // Customer Edit/Delete Modal State
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [showDeleteCustomerModal, setShowDeleteCustomerModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);

  // Group Management Modal State
  const [showGroupModal, setShowGroupModal] = useState(false);

  // Share Customer Dialog State
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareCustomerId, setShareCustomerId] = useState<string>('');
  const [shareCustomerName, setShareCustomerName] = useState<string>('');
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferCustomerId, setTransferCustomerId] = useState<string>('');
  const [transferCustomerName, setTransferCustomerName] = useState<string>('');

  // Customer chat (RAG) drawer
  const [chatCustomer, setChatCustomer] = useState<Customer | null>(null);

  // Audit log modals (Phase 2)
  const [auditCustomerId, setAuditCustomerId] = useState<string | null>(null);
  const [auditCustomerName, setAuditCustomerName] = useState<string>('');
  const [activityProjectId, setActivityProjectId] = useState<string | null>(null);
  const [activityProjectTitle, setActivityProjectTitle] = useState<string>('');

  // AMS360 Import Modal State
  const [showAMS360ImportModal, setShowAMS360ImportModal] = useState(false);
  const [isAms360Enabled, setIsAms360Enabled] = useState(false);

  // Project-related state
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  // Derive the initial view from the URL (not a hardcoded default) — on a
  // fresh full-page load (login redirect, browser refresh) the "state -> URL"
  // sync effect runs before the async "URL -> state" effect has resolved the
  // deep-linked customer/project. If this defaulted to 'customer-selection',
  // that sync effect would force-navigate to '/' and clobber the deep link
  // before it ever got a chance to load.
  const [currentNavigationView, setCurrentNavigationView] = useState<'customer-selection' | 'project-selection' | 'project-details'>(() => {
    if (urlProjectId) return 'project-details';
    if (urlCustomerId) return 'project-selection';
    return 'customer-selection';
  });
  
  // New customer form state
  const [newCustomerForm, setNewCustomerForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    status: 'active' as 'active' | 'inactive',
    lineType: 'commercial' as 'personal' | 'commercial',
    secondaryContact: ''
  });
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);
  const [includeAddress, setIncludeAddress] = useState(false);
  const [customerAddress, setCustomerAddress] = useState({
    street1: '',
    street2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'United States',
    addressType: 'primary',
    label: ''
  });
  
  // Customer detail view states
  // Derived from the URL's ?tab= param at mount, for the same reason
  // currentNavigationView is — avoids a flash of the wrong tab on deep link.
  const [customerDetailView, setCustomerDetailView] = useState<'workflows' | 'chats' | 'documents' | 'reports' | 'proposals' | 'insurance_summary'>(() => {
    const tab = searchParams.get('tab');
    if (tab && ['workflows', 'chats', 'documents', 'reports', 'proposals', 'insurance_summary'].includes(tab)) {
      return tab as 'workflows' | 'chats' | 'documents' | 'reports' | 'proposals' | 'insurance_summary';
    }
    return 'workflows';
  });
  // Deep-link summary id (set by URL handler; consumed once by InsuranceSummaryView).
  const [pendingInsuranceSummaryId, setPendingInsuranceSummaryId] = useState<string | null>(null);
  const [showContactOverlay, setShowContactOverlay] = useState(false);
  const [chatHistoryPage, setChatHistoryPage] = useState(0);
  const [reportsPage, setReportsPage] = useState(0);
  const [documentsPage, setDocumentsPage] = useState(0);
  const [editingReportId, setEditingReportId] = useState<string | null>(null);
  const [editingReportTitle, setEditingReportTitle] = useState('');
  const CHATS_PER_PAGE = 6;
  const REPORTS_PER_PAGE = 6;
  const DOCUMENTS_PER_PAGE = 6;
  
  // Chat session state
  const [chatTitle, setChatTitle] = useState('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  
  // Real customer data state
  const [customerDocuments, setCustomerDocuments] = useState<{[key: string]: any[]}>({});
  const [customerChats, setCustomerChats] = useState<{[key: string]: any[]}>({});
  const [customerReports, setCustomerReports] = useState<{[key: string]: UnderwritingReport[]}>({});
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [loadingCustomerData, setLoadingCustomerData] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  
  // Document selection and viewer state
  const [customerSelectedDocumentIds, setCustomerSelectedDocumentIds] = useState<string[]>([]);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [viewingDocument, setViewingDocument] = useState<PolicyDocument | null>(null);

  // Report selection state
  const [customerSelectedReportIds, setCustomerSelectedReportIds] = useState<string[]>([]);

  const { user, token } = useAuth();
  const {
    documents,
    setDocuments,
    selectedDocumentIds,
    selectedDocuments,
    setSelectedDocumentIds,
    toggleDocumentSelection,
    selectedPolicyType,
    setSelectedPolicyType,
    chatHistory,
    setChatHistory,
    addChatMessage,
    exportChatHistory,
    startNewSession,
    setCurrentCustomer,
    setCurrentProject,
    setCurrentChatSession,
    currentChatSessionId,
    refreshDocuments
  } = useDocuments();

  // Citation click state
  const [clickedCitation, setClickedCitation] = useState<CitationClick | null>(null);
  const [highlight, setHighlight] = useState<CitationHighlight | null>(null);
  
  // HTML PDF viewer state for citations
  const [showHtmlPdfViewer, setShowHtmlPdfViewer] = useState(false);
  const [htmlViewerCitation, setHtmlViewerCitation] = useState<ParsedCitation | null>(null);
  const [htmlViewerDocumentUrl, setHtmlViewerDocumentUrl] = useState<string>('');
  const [allCitationsOnPage, setAllCitationsOnPage] = useState<ParsedCitation[]>([]);
  const [citationMap, setCitationMap] = useState<Array<{
    docId: string;
    pageNumber: number;
    startOffset: number;
    endOffset: number;
    text: string;
  }>>([]);

  // API functions for customer data and reports
  const fetchCustomers = useCallback(async () => {
    try {
      setLoadingCustomers(true);
      const response = await fetch(`${API_BASE_URL}/api/customers`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const allCustomers = await response.json();
        
        // Separate customers, prospects, and suspects
        const customerList = allCustomers.filter((c: Customer) => c.type === 'customer');
        const prospectList = allCustomers.filter((c: Customer) => c.type === 'prospect');
        const suspectList = allCustomers.filter((c: Customer) => c.type === 'suspect');
        
        setCustomers(customerList);
        setProspects(prospectList);
        setSuspects(suspectList);
      } else {
        console.error('Failed to fetch customers:', await response.text());
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoadingCustomers(false);
    }
  }, [token]);

  const fetchComparisonReports = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/comparison-reports`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const reports = await response.json();
        setComparisonReports(reports);
      } else {
        console.error('Failed to fetch comparison reports:', await response.text());
      }
    } catch (error) {
      console.error('Error fetching comparison reports:', error);
    }
  }, [token]);

  // Function to save a new comparison report
  const saveComparisonReport = async (reportData: Omit<ComparisonReport, 'id' | 'createdAt' | 'createdBy'>) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/comparison-reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(reportData)
      });
      
      if (response.ok) {
        const newReport = await response.json();
        setComparisonReports(prev => [...prev, newReport]);
        devLog('📊 Comparison report saved successfully:', newReport.title);
        return newReport;
      } else {
        console.error('Failed to save comparison report:', await response.text());
      }
    } catch (error) {
      console.error('Error saving comparison report:', error);
    }
    return null;
  };

  // Create a new customer or prospect
  const createCustomer = async (customerData: {
    name: string;
    type: 'customer' | 'prospect' | 'suspect';
    email?: string;
    phone?: string;
    company?: string;
    status: 'active' | 'inactive' | 'lead';
  }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(customerData)
      });
      
      if (response.ok) {
        const newCustomer = await response.json();
        
        // Add to appropriate list based on type
        if (newCustomer.type === 'customer') {
          setCustomers(prev => [...prev, newCustomer]);
        } else if (newCustomer.type === 'prospect') {
          setProspects(prev => [...prev, newCustomer]);
        } else if (newCustomer.type === 'suspect') {
          setSuspects(prev => [...prev, newCustomer]);
        }
        
        devLog('👤 Customer created successfully:', newCustomer.name);
        return newCustomer;
      } else {
        console.error('Failed to create customer:', await response.text());
        return null;
      }
    } catch (error) {
      console.error('Error creating customer:', error);
      return null;
    }
  };

  // Load customers and reports on component mount
  useEffect(() => {
    if (token) {
      fetchCustomers();
      fetchComparisonReports();
    }
  }, [token, fetchCustomers, fetchComparisonReports]);

  // Check if AMS360 is enabled for the company
  useEffect(() => {
    const checkAms360Status = async () => {
      try {
        const settings = await ams360Service.getSettings();
        setIsAms360Enabled(settings.enabled);
      } catch (error) {
        // If we can't fetch settings, assume AMS360 is not enabled
        setIsAms360Enabled(false);
      }
    };
    if (token) {
      checkAms360Status();
    }
  }, [token]);

  // Legacy deep-link bridge: WorkflowReport.tsx (and any other pre-Phase-6
  // caller) still navigates to `/?customerId=&projectId=&view=project-details
  // &customerDetailView=&summaryId=`. Translate that one-shot query-param
  // format to the canonical path-based URL so it lands in the same place;
  // the effect below then owns state from the canonical URL.
  useEffect(() => {
    if (location.pathname !== '/') return;
    const qsCustomerId = searchParams.get('customerId');
    if (!qsCustomerId) return;

    const qsProjectId = searchParams.get('projectId');
    const qsView = searchParams.get('view');
    const qsDetailView = searchParams.get('customerDetailView') || 'workflows';
    const qsSummaryId = searchParams.get('summaryId');

    if (qsProjectId && qsView === 'project-details') {
      const tab = `tab=${qsDetailView}`;
      const summary = qsSummaryId ? `&summaryId=${encodeURIComponent(qsSummaryId)}` : '';
      navigate(`/customers/${qsCustomerId}/projects/${qsProjectId}?${tab}${summary}`, { replace: true });
    } else {
      navigate(`/customers/${qsCustomerId}`, { replace: true });
    }
  }, [location.pathname, searchParams, navigate]);

  // URL -> state. Drives selection from the canonical path (deep link, back/
  // forward, or a fresh load) rather than the other way around.
  useEffect(() => {
    if (!urlCustomerId) {
      if (currentNavigationView !== 'customer-selection') {
        setCurrentNavigationView('customer-selection');
        setSelectedCustomer(null);
        setSelectedProject(null);
        setSelectedReport(null);
        setCurrentCustomer('');
        setCurrentProject(null);
        setSelectedDocumentIds([]);
      }
      return;
    }

    const loadFromUrl = async () => {
      let customer = selectedCustomer?.id === urlCustomerId
        ? selectedCustomer
        : [...customers, ...prospects, ...suspects].find(c => c.id === urlCustomerId) || null;

      if (!customer) {
        try {
          const res = await fetch(`${API_BASE_URL}/api/customers/${urlCustomerId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            customer = await res.json();
          }
        } catch (error) {
          console.error('Error fetching customer from URL:', error);
        }
      }

      if (!customer) {
        // Lists may not have loaded yet on first render — wait for them
        // rather than bouncing the user immediately.
        if (customers.length === 0 && prospects.length === 0 && suspects.length === 0) return;
        showToast({ type: 'error', title: 'Not Found', message: 'That customer was not found or you no longer have access.', duration: 6000 });
        setCurrentNavigationView('customer-selection');
        setSelectedCustomer(null);
        setSelectedProject(null);
        navigate('/');
        return;
      }

      if (selectedCustomer?.id !== customer.id) {
        setSelectedCustomer(customer);
        setCurrentCustomer(customer.id);
        setSelectedDocumentIds([]);
      }

      if (!urlProjectId) {
        if (currentNavigationView !== 'project-selection') {
          setCurrentNavigationView('project-selection');
          setSelectedProject(null);
          setCurrentProject(null);
          setCustomerDetailView('workflows');
        }
        return;
      }

      if (selectedProject?.id === urlProjectId) {
        const tab = searchParams.get('tab');
        if (tab && ['workflows', 'chats', 'documents', 'reports', 'proposals', 'insurance_summary'].includes(tab) && customerDetailView !== tab) {
          setCustomerDetailView(tab as typeof customerDetailView);
        }
        return;
      }

      try {
        const project = await projectService.getProject(urlProjectId);
        if (!project || project.customerId !== urlCustomerId) {
          showToast({ type: 'error', title: 'Not Found', message: 'That project was not found or you no longer have access.', duration: 6000 });
          setCurrentNavigationView('project-selection');
          setSelectedProject(null);
          navigate(`/customers/${urlCustomerId}`);
          return;
        }

        setSelectedProject(project);
        setCurrentProject(project.id);
        setCurrentNavigationView('project-details');

        const tab = searchParams.get('tab');
        if (tab && ['workflows', 'chats', 'documents', 'reports', 'proposals', 'insurance_summary'].includes(tab)) {
          setCustomerDetailView(tab as typeof customerDetailView);
        } else {
          setCustomerDetailView('workflows');
        }
        const summaryId = searchParams.get('summaryId');
        if (summaryId) setPendingInsuranceSummaryId(summaryId);

        const title = `${customer.name} - ${project.title}`;
        setChatTitle(title);
        setIsEditingTitle(false);
        refreshDocuments();
        await fetchComparisonReports();
        try {
          await fetchProjectChats(project.id, urlCustomerId);
        } catch (error) {
          console.error('Error loading project chats from URL:', error);
        }
      } catch (error) {
        console.error('Error loading project from URL:', error);
        showToast({ type: 'error', title: 'Not Found', message: 'That project was not found or you no longer have access.', duration: 6000 });
        setCurrentNavigationView('project-selection');
        setSelectedProject(null);
        navigate(`/customers/${urlCustomerId}`);
      }
    };

    loadFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlCustomerId, urlProjectId, customers, prospects, suspects]);

  // state -> URL. Keeps the address bar in sync with in-app navigation
  // (clicking a customer/project/tab) without having to touch every
  // individual click handler.
  //
  // Ownership rule: whichever side actually changed wins. When the URL is what
  // changed, the "URL -> state" effect above owns reconciliation and this one
  // must stand down — its setState calls are queued, not applied, so this
  // effect would still observe the previous render's selection and navigate
  // right back. That is exactly what made clicking "Home" bounce: navigate('/')
  // cleared the route, the reset above was queued, and this effect re-pushed
  // /customers/:id/projects/:id from stale state before the reset landed.
  const lastSyncedPathRef = useRef(location.pathname);
  useEffect(() => {
    const urlChanged = lastSyncedPathRef.current !== location.pathname;
    lastSyncedPathRef.current = location.pathname;
    if (urlChanged) return;

    if (currentNavigationView === 'customer-selection') {
      if (location.pathname !== '/') navigate('/', { replace: true });
      return;
    }
    if (!selectedCustomer) return;

    if (currentNavigationView === 'project-selection' || !selectedProject) {
      const target = `/customers/${selectedCustomer.id}`;
      if (location.pathname !== target) navigate(target, { replace: true });
      return;
    }

    if (currentNavigationView === 'project-details') {
      const target = `/customers/${selectedCustomer.id}/projects/${selectedProject.id}`;
      const currentTab = searchParams.get('tab');
      if (location.pathname !== target || currentTab !== customerDetailView) {
        navigate(`${target}?tab=${customerDetailView}`, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNavigationView, selectedCustomer?.id, selectedProject?.id, customerDetailView, location.pathname]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl/Cmd + K to focus chat input
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        const chatInput = document.querySelector('textarea[placeholder*="message"]') as HTMLTextAreaElement;
        if (chatInput) {
          chatInput.focus();
        }
      }
      
      // F1 replays the product tour
      if (event.key === 'F1') {
        event.preventDefault();
        startTour();
      }
      
      // Ctrl/Cmd + U to show upload
      if ((event.ctrlKey || event.metaKey) && event.key === 'u') {
        event.preventDefault();
        setShowUploader(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [startTour]);

  // Prevent AI features for non-licensed users
  if (user?.status !== 'active') {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center">
          <h2 className="text-lg font-bold text-secondary dark:text-dark-text mb-2">
            License Required
          </h2>
          <p className="text-accent dark:text-dark-muted">
            Your account is pending activation by an administrator. AI features are disabled.
          </p>
        </div>
      </div>
    );
  }

  // Customer/Prospect handlers
  const handleCreateCustomer = async (customerData: Omit<Customer, 'id' | 'createdAt'>) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(customerData)
      });

      if (response.ok) {
        const newCustomer = await response.json();
        
        // Refresh the customer list to include the new customer
        await fetchCustomers();
        
        setShowNewCustomerModal(false);
        
        // Optionally select the newly created customer
        setSelectedCustomer(newCustomer);
        setCurrentCustomer(newCustomer.id);
        setReportsPage(0); // Reset reports pagination
      } else {
        console.error('Failed to create customer:', await response.text());
      }
    } catch (error) {
      console.error('Error creating customer:', error);
    }
  };

  const handleSubmitCustomerForm = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newCustomerForm.lineType === 'commercial' && !newCustomerForm.company.trim()) {
      alert('Please enter a company name');
      return;
    }

    if (!newCustomerForm.name.trim()) {
      alert(newCustomerForm.lineType === 'personal' ? 'Please enter a primary contact name' : 'Please enter a contact name');
      return;
    }
    
    setIsCreatingCustomer(true);
    
    try {
      const customerData: any = {
        name: newCustomerForm.name.trim(),
        type: newCustomerType,
        email: newCustomerForm.email.trim() || undefined,
        phone: newCustomerForm.phone.trim() || undefined,
        company: newCustomerForm.lineType === 'commercial' ? (newCustomerForm.company.trim() || undefined) : undefined,
        lineType: newCustomerForm.lineType,
        secondaryContact: newCustomerForm.lineType === 'personal' ? (newCustomerForm.secondaryContact.trim() || undefined) : undefined,
        status: newCustomerForm.status
      };

      // Include address if provided and user checked the box
      if (includeAddress && customerAddress.street1.trim()) {
        customerData.address = {
          street1: customerAddress.street1.trim(),
          street2: customerAddress.street2.trim() || '',
          city: customerAddress.city.trim(),
          state: customerAddress.state.trim(),
          postalCode: customerAddress.postalCode.trim(),
          country: customerAddress.country || 'United States',
          addressType: customerAddress.addressType || 'primary',
          label: customerAddress.label.trim() || ''
        };
      }
      
      const result = await createCustomer(customerData);
      
      if (result) {
        // Reset form and close modal
        setNewCustomerForm({
          name: '',
          email: '',
          phone: '',
          company: '',
          status: 'active',
          lineType: 'commercial',
          secondaryContact: ''
        });
        setCustomerAddress({
          street1: '',
          street2: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'United States',
          addressType: 'primary',
          label: ''
        });
        setIncludeAddress(false);
        setShowNewCustomerModal(false);
      } else {
        alert('Failed to create customer. Please try again.');
      }
    } catch (error) {
      console.error('Error in handleSubmitCustomerForm:', error);
      alert('An error occurred while creating the customer.');
    } finally {
      setIsCreatingCustomer(false);
    }
  };

  const handleSelectCustomer = async (customer: Customer) => {
    devLog('👤 Selecting customer:', customer.name, customer.id);
    setSelectedCustomer(customer);
    setSelectedReport(null); // Clear any selected report
    setSelectedProject(null); // Clear any selected project
    
    // Set current customer in DocumentContext to enable chat session association
    setCurrentCustomer(customer.id);
    // Clear previous documents when switching customers
    setSelectedDocumentIds([]);
    
    // Navigate to project selection view
    setCurrentNavigationView('project-selection');
    
    // Reset customer detail view states for when we eventually get to project details
    setCustomerDetailView('workflows');
    setShowContactOverlay(false);
    setChatHistoryPage(0);
    setReportsPage(0);
    setDocumentsPage(0);
    
    devLog('👤 Navigating to project selection for customer:', customer.name);
  };

  const handleSelectProject = async (project: Project, opts?: { forceWorkflowsTab?: boolean }) => {
    devLog('📁 Selecting project:', project.title, project.id);
    setSelectedProject(project);

    // Set current project in DocumentContext to enable project-scoped data
    devLog('📁 Setting current project to:', project.id);
    setCurrentProject(project.id);

    // Navigate to project details view
    setCurrentNavigationView('project-details');

    // The recommendation-pill click target always lands on Workflows,
    // regardless of whichever tab this session last remembered.
    if (opts?.forceWorkflowsTab) {
      setCustomerDetailView('workflows');
    }

    // Set default chat title for this project
    const title = `${selectedCustomer?.name} - ${project.title}`;
    setChatTitle(title);
    setIsEditingTitle(false);

    // Refresh documents for this project - DocumentContext will now filter by projectId
    refreshDocuments();
    
    // Refresh comparison reports to get the latest reports for all projects
    await fetchComparisonReports();

    // Fetch project-specific data
    setLoadingCustomerData(true);
    try {
      const [chats, reports] = await Promise.all([
        fetchProjectChats(project.id, project.customerId),
        fetchProjectReports(project.id)
      ]);
      devLog('📁 Project data loaded - Chats:', chats.length, 'Reports:', reports.length);
    } catch (error) {
      console.error('Error loading project data:', error);
    } finally {
      setLoadingCustomerData(false);
    }
  };

  const handleBackToCustomers = () => {
    devLog('⬅️ Navigating back to customer selection');
    setSelectedCustomer(null);
    setSelectedProject(null);
    setCurrentNavigationView('customer-selection');
    
    // Clear document context
    setCurrentCustomer('');
    setCurrentProject(null); // CRITICAL: Clear project to prevent cross-contamination
    setSelectedDocumentIds([]);
  };

  const handleBackToProjects = () => {
    devLog('⬅️ Navigating back to project selection');
    setSelectedProject(null);
    setCurrentNavigationView('project-selection');
    
    // CRITICAL: Clear the current project in DocumentContext to prevent cross-contamination
    setCurrentProject(null);
    // Also clear selected documents when navigating back to projects
    setSelectedDocumentIds([]);
  };

  // Project-specific data fetching functions
  const fetchProjectDocuments = async (projectId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/documents?projectId=${projectId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
        }
      });
      if (response.ok) {
        const documents = await response.json();
        // BUG: setCustomerDocuments is typed {[customerId: string]: any[]} and read as
        // customerDocuments[customerId] elsewhere, but this clobbers it with a raw array.
        // Pre-existing; fixing it needs an audit of every call site of fetchProjectDocuments.
        setCustomerDocuments(documents);
        return documents;
      }
    } catch (error) {
      console.error('Error fetching project documents:', error);
    }
    return [];
  };

  const fetchProjectChats = async (projectId: string, customerId?: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/sessions-by-project/${projectId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        const chats = data.sessions || [];
        const key = customerId || selectedCustomer?.id;
        if (key) {
          setCustomerChats(prev => ({ ...prev, [key]: chats }));
        }
        return chats;
      }
    } catch (error) {
      console.error('Error fetching project chats:', error);
    }
    return [];
  };

  const fetchProjectReports = async (projectId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/comparison-reports?projectId=${projectId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
        }
      });
      if (response.ok) {
        const reports = await response.json();
        setProjectReports(reports);
        return reports;
      }
    } catch (error) {
      console.error('Error fetching project reports:', error);
    }
    return [];
  };

  const handleSelectReport = async (report: ComparisonReport) => {
    devLog('📊 Opening comparison report:', report.title, report.id);
    devLog('📊 Full report object:', JSON.stringify(report, null, 2));
    
    const reportDataPayload = {
      report: report.content,
      reportTitle: report.title,
      documentIds: report.documentIds,
      documentNames: report.documentNames,
      primaryPolicyType: report.primaryPolicyType,
      additionalFacts: report.additionalFacts,
      projectId: report.projectId,
      isExistingReport: true
    };
    
    devLog('📊 Report data payload:', JSON.stringify(reportDataPayload, null, 2));
    
    // Store report data in sessionStorage as backup
    sessionStorage.setItem('comparison-report-data', JSON.stringify(reportDataPayload));
    devLog('💾 Stored in sessionStorage');
    
    // Navigate to comparison page with both state and URL param
    devLog('🚀 Navigating to /comparison');
    navigate(`/comparison?reportId=${report.id}`, {
      state: {
        reportData: reportDataPayload
      }
    });
    devLog('✅ Navigation called');
  };

  const handleSelectReportForChat = async (report: ComparisonReport) => {
    devLog('💬 Selecting comparison report for chat:', report.title, report.id);
    setSelectedReport(report);
    setSelectedCustomer(null); // Clear any selected customer
    setCurrentCustomer(null);
    
    // Set documents from the report
    setSelectedDocumentIds(report.documentIds);
    
    // Set chat title and initialize with report context
    setChatTitle(report.title);
    setIsEditingTitle(false);
    
    // Initialize chat with report context
    const initialMessage: ChatMessage = {
      id: `msg_${Date.now()}`,
      content: `I've loaded the comparison report "${report.title}". This report analyzes ${report.documentNames.join(', ')}. Feel free to ask me any questions about the findings or the original documents!`,
      sender: 'ai',
      timestamp: new Date()
    };
    
    setChatHistory([initialMessage]);
    startNewSession();
  };

  const handleEditReportTitle = (reportId: string, currentTitle: string) => {
    setEditingReportId(reportId);
    setEditingReportTitle(currentTitle);
  };

  const handleSaveReportTitle = async (reportId: string) => {
    if (!selectedCustomer || !editingReportTitle.trim()) {
      setEditingReportId(null);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/customers/${selectedCustomer.id}/reports/${reportId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: editingReportTitle.trim()
        })
      });

      if (response.ok) {
        const newTitle = editingReportTitle.trim();

        // Update the report in all local states that might contain it
        setCustomerReports(prev => ({
          ...prev,
          [selectedCustomer.id]: (prev[selectedCustomer.id] || []).map((report: any) =>
            report.id === reportId
              ? { ...report, title: newTitle }
              : report
          )
        }));

        // Also update projectReports if viewing project details
        setProjectReports(prev => prev.map((report: any) =>
          report.id === reportId
            ? { ...report, title: newTitle }
            : report
        ));

        // Also update comparisonReports
        setComparisonReports(prev => prev.map((report: any) =>
          report.id === reportId
            ? { ...report, title: newTitle }
            : report
        ));
      } else {
        console.error('Failed to update report title');
      }
    } catch (error) {
      console.error('Error updating report title:', error);
    } finally {
      setEditingReportId(null);
      setEditingReportTitle('');
    }
  };

  const handleConvertToCustomer = async (prospectId: string) => {
    try {
      const prospect = prospects.find(p => p.id === prospectId);
      if (!prospect) return;

      // Update the prospect to be a customer via API
      const response = await fetch(`${API_BASE_URL}/api/customers/${prospectId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...prospect,
          type: 'customer',
          status: 'active',
          lastContact: new Date().toISOString()
        })
      });

      if (response.ok) {
        const updatedCustomer = await response.json();
        
        // Refresh the customer list
        await fetchCustomers();
        
        // Update selected customer if it was the converted prospect
        if (selectedCustomer?.id === prospectId) {
          setSelectedCustomer(updatedCustomer);
          setCurrentCustomer(updatedCustomer.id);
        }
      } else {
        console.error('Failed to convert prospect:', await response.text());
      }
    } catch (error) {
      console.error('Error converting prospect:', error);
    }
  };

  // Customer Edit/Delete Handlers
  const handleEditCustomer = (customer: Customer) => {
    setEditingCustomer(customer);
    setShowEditCustomerModal(true);
  };

  const handleDeleteCustomer = (customer: Customer) => {
    setEditingCustomer(customer);
    setShowDeleteCustomerModal(true);
  };

  const handleShareCustomer = (customer: Customer) => {
    setShareCustomerId(customer.id);
    setShareCustomerName(getCustomerDisplayName(customer));
    setShowShareDialog(true);
  };

  const handleTransferCustomer = (customer: Customer) => {
    setTransferCustomerId(customer.id);
    setTransferCustomerName(getCustomerDisplayName(customer));
    setShowTransferDialog(true);
  };

  const handleSaveCustomer = async (customerId: string, data: Partial<Customer>) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/customers/${customerId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        // Refresh customer data
        await fetchCustomers();
        setShowEditCustomerModal(false);
        setEditingCustomer(null);
      } else {
        throw new Error('Failed to update customer');
      }
    } catch (error) {
      console.error('Error updating customer:', error);
      throw error;
    }
  };

  const handleConfirmDeleteCustomer = async (customerId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/customers/${customerId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Refresh customer data
        await fetchCustomers();
        setShowDeleteCustomerModal(false);
        setEditingCustomer(null);

        // If the deleted customer was selected, clear selection
        if (selectedCustomer?.id === customerId) {
          setSelectedCustomer(null);
        }
      } else {
        throw new Error('Failed to delete customer');
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
      throw error;
    }
  };

  const getCustomerDisplayKey = (c: Customer): string => {
    const name = c.lineType === 'personal' ? c.name : (c.company || c.name);
    return (name || '').toLowerCase();
  };

  const matchesCustomerFilters = (c: Customer): boolean => {
    if (customerFilters.lineType !== 'all' && c.lineType !== customerFilters.lineType) return false;
    if (customerFilters.status !== 'all' && c.status !== customerFilters.status) return false;
    if (customerFilters.ownership === 'mine' && c.isOwner === false) return false;
    if (customerFilters.ownership === 'shared' && c.isOwner !== false) return false;
    if (customerFilters.ams360 === 'synced' && !c.ams360CustomerId) return false;
    if (customerFilters.ams360 === 'unsynced' && !!c.ams360CustomerId) return false;
    return true;
  };

  const customerMatchesSearch = (c: Customer, q: string): boolean => (
    c.name?.toLowerCase().includes(q) ||
    !!c.company?.toLowerCase().includes(q) ||
    !!c.email?.toLowerCase().includes(q) ||
    !!c.phone?.toLowerCase().includes(q) ||
    !!c.secondaryContact?.toLowerCase().includes(q)
  );

  const getCurrentList = (): (Customer | ComparisonReport)[] => {
    let list: (Customer | ComparisonReport)[];

    if (selectedView === 'all' || selectedView === 'customers' || selectedView === 'prospects' || selectedView === 'suspects') {
      const base: Customer[] =
        selectedView === 'all' ? [...customers, ...prospects, ...suspects] :
        selectedView === 'customers' ? customers :
        selectedView === 'prospects' ? prospects :
        suspects;

      let filtered: Customer[] = base.filter(matchesCustomerFilters);

      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        filtered = filtered.filter(c => customerMatchesSearch(c, q));
      }

      // Apply sort
      if (sortOrder !== 'default') {
        filtered = [...filtered].sort((a, b) => {
          switch (sortOrder) {
            case 'alpha-asc':
              return getCustomerDisplayKey(a).localeCompare(getCustomerDisplayKey(b));
            case 'alpha-desc':
              return getCustomerDisplayKey(b).localeCompare(getCustomerDisplayKey(a));
            case 'newest':
              return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            case 'oldest':
              return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
            case 'last-contact': {
              const ta = a.lastContact ? new Date(a.lastContact).getTime() : -Infinity;
              const tb = b.lastContact ? new Date(b.lastContact).getTime() : -Infinity;
              return tb - ta;
            }
            default:
              return 0;
          }
        });
      }

      list = filtered;
    } else {
      // Comparison reports view — filters don't apply
      let reports = comparisonReports;
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        reports = reports.filter(item =>
          item.title.toLowerCase().includes(q) ||
          item.content.toLowerCase().includes(q) ||
          item.documentNames.some(name => name.toLowerCase().includes(q)) ||
          item.primaryPolicyType.toLowerCase().includes(q)
        );
      }
      if (sortOrder === 'alpha-asc' || sortOrder === 'alpha-desc') {
        reports = [...reports].sort((a, b) => {
          const cmp = a.title.toLowerCase().localeCompare(b.title.toLowerCase());
          return sortOrder === 'alpha-asc' ? cmp : -cmp;
        });
      } else if (sortOrder === 'newest' || sortOrder === 'oldest') {
        reports = [...reports].sort((a, b) => {
          const diff = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          return sortOrder === 'newest' ? diff : -diff;
        });
      }
      list = reports;
    }

    return list;
  };

  const handleFilesUploaded = async (newFiles: UploadedFile[]) => {
    devLog('📄 Files uploaded:', newFiles.length);
    // FileUploader has already POSTed these documents, with the batch-rename
    // title and document type applied. This callback must NOT upload again —
    // it used to open a second "Document Details" modal that re-sent the file
    // without a projectId, which the backend rejects (400 PROJECT_ID_REQUIRED).
    // Nothing left to send here; just close the uploader and refresh.
    setShowUploader(false);
    // Refresh through the same path drag-and-drop uses (onDocumentsChanged).
    // In a project folder the list is owned by DocumentContext, so it has to be
    // refreshDocuments() — fetchProjectDocuments() writes to customerDocuments
    // and leaves the folder's document list stale.
    if (currentNavigationView === 'project-details' && selectedProject?.id) {
      refreshDocuments();
    } else if (selectedCustomer) {
      await fetchCustomerDocuments(selectedCustomer.id);
    }
  };

  const generateMessageId = () => `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Handle chat messages when a comparison report is selected
  const handleReportChatMessage = async (message: string) => {
    if (!selectedReport) return;

    // Add user message
    const userMessage: ChatMessage = {
      id: generateMessageId(),
      content: message,
      sender: 'user',
      timestamp: new Date()
    };
    
    setChatHistory(prev => [...prev, userMessage]);

    // Get selected documents for context
    const reportDocuments = documents.filter(doc => selectedReport.documentIds.includes(doc.id));
    
    // Create comprehensive context with both the report AND the original documents
    const documentTexts = reportDocuments.map((doc, index) =>
      `DOCUMENT ${index + 1}: ${doc.name} (Policy Type: ${doc.policyType || 'Unknown'})
${doc.extractedText || 'Unable to extract text from this document.'}`
    ).join('\n\n---\n\n');

    const contextualPrompt = `
Based on the following comparison report, original documents, and user question, provide a helpful response:

COMPARISON REPORT: "${selectedReport.title}"
${selectedReport.content}

ORIGINAL DOCUMENTS:
${documentTexts}

USER QUESTION: ${message}

Please provide a detailed and helpful response based on both the comparison report and the original document content. You can reference specific details from either the report or the original documents as needed.
`;

    // Create AI message with streaming
    const aiMessageId = generateMessageId();
    const aiMessage: ChatMessage = {
      id: aiMessageId,
      content: '',
      sender: 'ai',
      timestamp: new Date(),
      isStreaming: true
    };
    
    setChatHistory(prev => [...prev, aiMessage]);

    try {
      let finalAiContent = '';
      await aiService.sendRawPrompt(contextualPrompt, (streamContent) => {
        finalAiContent = streamContent;
        setChatHistory(prev =>
          prev.map(msg =>
            msg.id === aiMessageId ? { ...msg, content: streamContent } : msg
          )
        );
      });

      // Mark streaming as complete
      const finalAiMessage: ChatMessage = {
        id: aiMessageId,
        content: finalAiContent,
        sender: 'ai',
        timestamp: new Date(),
        isStreaming: false
      };

      setChatHistory(prev => 
        prev.map(msg => 
          msg.id === aiMessageId 
            ? finalAiMessage
            : msg
        )
      );

    } catch (error) {
      console.error('Report chat error:', error);
      const errorMessage: ChatMessage = {
        id: aiMessageId,
        content: 'Sorry, I encountered an error. Please try again.',
        sender: 'ai',
        timestamp: new Date(),
        isStreaming: false
      };

      setChatHistory(prev => 
        prev.map(msg => 
          msg.id === aiMessageId 
            ? errorMessage
            : msg
        )
      );
    }
  };

  const handleSendMessage = async (message: string) => {
    // If a comparison report is selected, use report-specific context
    if (selectedReport) {
      return handleReportChatMessage(message);
    }
    
    // Fetch full document data with extractedText
    const documentsWithText = await Promise.all(
      selectedDocuments.map(async (doc) => {
        try {
          // If document already has extractedText, use it
          if (doc.extractedText) {
            return {
              id: doc.id,
              name: doc.name,
              extractedText: doc.extractedText
            };
          }
          
          // Otherwise, fetch the full document data
          const response = await fetch(`${API_BASE_URL}/api/documents/${doc.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            const fullDoc = await response.json();
            devLog(`📄 Fetched full document ${doc.name}:`, {
              hasExtractedText: !!fullDoc.extractedText,
              extractedTextLength: fullDoc.extractedText ? fullDoc.extractedText.length : 0
            });
            return {
              id: doc.id,
              name: doc.name,
              extractedText: fullDoc.extractedText || ''
            };
          } else {
            console.error(`Failed to fetch full document ${doc.id}`);
            return {
              id: doc.id,
              name: doc.name,
              extractedText: ''
            };
          }
        } catch (error) {
          console.error(`Error fetching document ${doc.id}:`, error);
          return {
            id: doc.id,
            name: doc.name,
            extractedText: ''
          };
        }
      })
    );
    
    // Build current context for validation
    const currentContext: ChatContext = {
      messages: chatHistory,
      documents: documentsWithText,
      policyType: selectedPolicyType,
      customerId: selectedCustomer?.id,
      sessionId: currentChatSessionId || undefined
    };

    // Validate context and show appropriate warnings/errors
    const validation = chatContextService.validateContext(currentContext);
    
    if (!validation.isValid) {
      // Show error messages to user
      for (const error of validation.errors) {
        const errorMsg: ChatMessage = {
          id: generateMessageId(),
          content: error,
          sender: 'ai',
          timestamp: new Date(),
          isStreaming: false
        };
        setChatHistory(prev => [...prev, errorMsg]);
      }
      return;
    }

    // Show warnings if any (but continue with the request)
    if (validation.warnings.length > 0) {
      devLog('Context warnings:', validation.warnings);
      // Could optionally show warnings to user as well
    }

    // Log context summary for debugging
    devLog('📋 Chat Context Summary:', chatContextService.generateContextSummary(currentContext));
    // Add user message
    const userMessage: ChatMessage = {
      id: generateMessageId(),
      content: message,
      sender: 'user',
      timestamp: new Date()
    };
    
    // Only pass title if we're starting a new conversation (no current session)
    const titleForNewSession = !currentChatSessionId ? (chatTitle.trim() || undefined) : undefined;
    // Create or reuse session and send user message
    const sessionId = await addChatMessage(userMessage, titleForNewSession);
    setIsLoading(true);

    // Create AI message with streaming
    const aiMessageId = generateMessageId();
    const aiMessage: ChatMessage = {
      id: aiMessageId,
      content: '',
      sender: 'ai',
      timestamp: new Date(),
      isStreaming: true
    };
    
    // Add AI message to local state for immediate UI update
    setChatHistory((prev: ChatMessage[]) => [...prev, aiMessage]);

    try {
      // Build raw prompt with document spans and conversation history
      const contextData = chatContextService.buildFullContext(currentContext);
      // Fetch spans for each document
      const spanBlocks = await Promise.all(
        contextData.documents.map(async doc => {
          const res = await fetch(`${API_BASE_URL}/api/documents/${doc.id}/spans`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const spans = res.ok ? await res.json() : [];
          return spans
            .map((span: any) =>
              `[CITATION:${doc.id},${span.pageNumber},${span.startOffset},${span.endOffset}]\n${span.text}`
            )
            .join('\n');
        })
      );
      const docContext = spanBlocks.join('\n\n');
      // Build conversation history text
      const convHistory = contextData.conversationHistory
        .map(h => `${h.sender.toUpperCase()}: ${h.content}`)
        .join('\n');
      // Use Smart AI service with citations when documents are selected
      let finalAiContent = '';
      let responseCitations: any[] = [];
      
      if (contextData.documents.length > 0) {
        // Use Smart AI service for document-based chat with citations  
        try {
          devLog('🚀 FINAL MESSAGE BEING SENT TO BACKEND:', message);
          devLog('🚀 Message length:', message.length);
          devLog('🚀 Message preview:', message.substring(0, 200) + '...');
          
          const requestBody = {
            message: message,
            documents: contextData.documents.map(doc => ({
              id: doc.id,
              name: doc.name,
              extractedText: doc.extractedText || ''
            })),
            sessionId: sessionId,
            options: {
              smartCitations: true,
              enhanceCitations: true,
              validateCitations: true
            }
          };
          
          devLog('🌐 ACTUAL HTTP REQUEST BODY:', JSON.stringify(requestBody, null, 2));
          devLog('📄 Document content being sent:', requestBody.documents.map(d => ({
            name: d.name,
            textLength: d.extractedText.length,
            preview: d.extractedText.substring(0, 100) + '...'
          })));
          
          const smartResponse = await fetch(`${API_BASE_URL}/api/chat/smart`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(requestBody)
          });
          
          if (smartResponse.ok) {
            const smartResult = await smartResponse.json();
            finalAiContent = smartResult.response || smartResult.message || '';
            responseCitations = smartResult.citations || [];
            devLog(`📊 Home chat received ${responseCitations.length} citations from Smart AI`);
            
            // Update streaming display
            setChatHistory(prev =>
              prev.map(msg =>
                msg.id === aiMessageId ? { ...msg, content: finalAiContent } : msg
              )
            );
          } else {
            throw new Error('Smart AI service failed');
          }
        } catch (smartError) {
          console.warn('Smart AI service failed, falling back to basic AI:', smartError);
          // Fallback to basic AI service
          const rawPrompt = `${docContext}\n\n${convHistory}\nUSER: ${message}`;
          await aiService.sendRawPrompt(rawPrompt, streamContent => {
            finalAiContent = streamContent;
            setChatHistory(prev =>
              prev.map(msg =>
                msg.id === aiMessageId ? { ...msg, content: streamContent } : msg
              )
            );
          });
        }
      } else {
        // No documents selected, use basic AI service
        devLog('🚀 FINAL MESSAGE (NO DOCS) BEING SENT TO BACKEND:', message);
        devLog('🚀 Message length:', message.length);
        devLog('🚀 Message preview:', message.substring(0, 200) + '...');
        
        const rawPrompt = `${convHistory}\nUSER: ${message}`;
        await aiService.sendRawPrompt(rawPrompt, streamContent => {
          finalAiContent = streamContent;
          setChatHistory(prev =>
            prev.map(msg =>
              msg.id === aiMessageId ? { ...msg, content: streamContent } : msg
            )
          );
        });
      }

      // Mark streaming as complete and save final AI message to backend
      const finalAiMessage: ChatMessage = {
        id: aiMessageId,
        content: finalAiContent,
        sender: 'ai',
        timestamp: new Date(),
        isStreaming: false,
        citations: responseCitations
      };

      setChatHistory((prev: ChatMessage[]) => 
        prev.map(msg => 
          msg.id === aiMessageId 
            ? finalAiMessage
            : msg
        )
      );

      // Save AI message to backend (reuse existing session)
      await addChatMessage(finalAiMessage, undefined, sessionId || undefined);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage: ChatMessage = {
        id: aiMessageId,
        content: 'Sorry, I encountered an error. Please try again.',
        sender: 'ai',
        timestamp: new Date(),
        isStreaming: false
      };

      setChatHistory((prev: ChatMessage[]) => 
        prev.map(msg => 
          msg.id === aiMessageId 
            ? errorMessage
            : msg
        )
      );

      // Save error message to backend
      await addChatMessage(errorMessage, undefined, sessionId || undefined);
    } finally {
      setIsLoading(false);
    }
  };

  // Handler for Risk Assessment using policy-specific prompts
  const handleRiskAssessment = async () => {
    devLog('⚙️ handleRiskAssessment invoked. Selected Documents:', selectedDocuments);
    if (!selectedCustomer || selectedDocuments.length === 0) return;
    setIsLoading(true);
    
    // Build citation map from all selected documents
    const allSpans: Array<{
      docId: string;
      pageNumber: number;
      startOffset: number;
      endOffset: number;
      text: string;
    }> = [];
    
    for (const doc of selectedDocuments) {
      devLog('⚙️ Preparing analysis for document:', doc.name, 'policyType:', doc.policyType, 'extractedText length:', doc.extractedText?.length);
      
      // Import the prompt mapping utilities
      const { getAnalysisPrompt } = await import('../utils/promptMapping');
      
      // Get the appropriate prompt based on the document's policy type
      const policyType = doc.policyType || 'general-liability';
      const promptFunction = getAnalysisPrompt(policyType);
      const systemPrompt = promptFunction([doc.name]);
      
      // Fetch spans for citation support
      const res = await fetch(`${API_BASE_URL}/api/documents/${doc.id}/spans`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const spans = res.ok ? await res.json() : [];
      
      // Add spans to citation map
      for (const span of spans) {
        allSpans.push({
          docId: doc.id,
          pageNumber: span.pageNumber,
          startOffset: span.startOffset,
          endOffset: span.endOffset,
          text: span.text
        });
      }
      
      const spanBlocks = spans
        .map((span: any) =>
          `[CITATION:${doc.id},${span.pageNumber},${span.startOffset},${span.endOffset}]\n${span.text}`
        )
        .join('\n');
      
      const documentText = spanBlocks || `Document: ${doc.name}\n${doc.extractedText || 'Unable to extract text from this document.'}`;
      const fullPrompt = `${systemPrompt}\n\n${documentText}`;
      devLog('⚙️ Document ID:', doc.id, 'Document Name:', doc.name);
      devLog('⚙️ First few spans:', spans.slice(0, 3));
      devLog('⚙️ First 500 chars of spanBlocks:', spanBlocks.substring(0, 500));
      // Notify user of analysis action with title
      const titleMsg: ChatMessage = {
        id: generateMessageId(),
        content: `Document Analysis: ${doc.name}`,
        sender: 'user',
        timestamp: new Date()
      };
      setChatHistory(prev => [...prev, titleMsg]);
      // Don't save this system-generated message to the backend chat session
      
      // Set citation map before streaming response
      setCitationMap(allSpans);
      
      // AI message streaming
      const aiMessageId = generateMessageId();
      const aiMessage: ChatMessage = {
        id: aiMessageId,
        content: '',
        sender: 'ai',
        timestamp: new Date(),
        isStreaming: true
      };
      setChatHistory(prev => [...prev, aiMessage]);
      try {
        let finalContent = '';
        let analysisCitations: any[] = [];
        
        // Use Smart AI service for document analysis with citations
        try {
          const smartResponse = await fetch(`${API_BASE_URL}/api/chat/smart`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              message: `Analyze this ${policyType} policy document for risks, coverage gaps, and recommendations.`,
              documents: [{
                id: doc.id,
                name: doc.name,
                extractedText: doc.extractedText || '',
                policyType: doc.policyType || undefined,
                effectiveDate: doc.uploadedAt || new Date(),
                sections: []
              }],
              options: {
                smartCitations: true,
                enhanceCitations: true,
                validateCitations: true
              }
            })
          });
          
          if (smartResponse.ok) {
            const smartResult = await smartResponse.json();
            finalContent = smartResult.response || smartResult.message || '';
            analysisCitations = smartResult.citations || [];
            devLog(`📊 Home analysis received ${analysisCitations.length} citations from Smart AI`);
            
            // Update streaming display
            setChatHistory(prev => prev.map(msg => msg.id === aiMessageId ? { ...msg, content: finalContent } : msg));
          } else {
            throw new Error('Smart analysis service failed');
          }
        } catch (smartError) {
          console.warn('Smart analysis service failed, falling back to basic AI:', smartError);
          // Fallback to basic AI service
          await aiService.sendRawPrompt(fullPrompt, (streamContent) => {
            finalContent = streamContent;
            setChatHistory(prev => prev.map(msg => msg.id === aiMessageId ? { ...msg, content: streamContent } : msg));
          });
        }
        
        const completedMessage: ChatMessage = { 
          ...aiMessage, 
          content: finalContent, 
          isStreaming: false,
          citations: analysisCitations
        };
        setChatHistory(prev => prev.map(msg => msg.id === aiMessageId ? completedMessage : msg));
        // Save only the AI response to the backend, not the system-generated title message
        await addChatMessage(completedMessage, `Document Analysis: ${doc.name}`);
      } catch (error) {
        console.error('Risk Assessment error:', error);
      }
    }
    setIsLoading(false);
  };

  // Handler for Compare & Optimize that uses policy-specific comparison prompts
  const handleComparePolicies = async () => {
    if (!selectedCustomer || selectedDocuments.length < 2) return;
    setIsLoading(true);
    
    // Import the prompt mapping utilities
    const { getComparisonPrompt, determinePrimaryPolicyType } = await import('../utils/promptMapping');
    
    // Determine the primary policy type from selected documents
    const primaryPolicyType = determinePrimaryPolicyType(selectedDocuments);
    devLog('🔍 Primary policy type for comparison:', primaryPolicyType, 'from documents:', selectedDocuments.map(d => ({ name: d.name, policyType: d.policyType })));
    
    // Build document text context with spans for citations
    const documentTexts = await Promise.all(
      selectedDocuments.map(async (doc) => {
        const res = await fetch(`${API_BASE_URL}/api/documents/${doc.id}/spans`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const spans = res.ok ? await res.json() : [];
        const spanBlocks = spans
          .map((span: any) =>
            `[CITATION:${doc.id},${span.pageNumber},${span.startOffset},${span.endOffset}]\n${span.text}`
          )
          .join('\n');
        
        return spanBlocks || `Document: ${doc.name} (Policy Type: ${doc.policyType || 'Unknown'})\n${doc.extractedText || 'Unable to extract text from this document.'}`;
      })
    );
    
    // Get the appropriate comparison prompt based on the primary policy type
    const comparisonPromptFunction = getComparisonPrompt(primaryPolicyType);
    const systemPrompt = comparisonPromptFunction(selectedDocuments.map(d => d.name));
    const fullPrompt = `${systemPrompt}\n\n${documentTexts.join('\n\n')}`;
    
    // Notify user of comparison action with title
    const docNames = selectedDocuments.map(doc => doc.name).join(', ');
    const titleMsg: ChatMessage = {
      id: generateMessageId(),
      content: `Compare Policies: ${docNames}`,
      sender: 'user',
      timestamp: new Date()
    };
    setChatHistory(prev => [...prev, titleMsg]);
    // Don't save this system-generated message to the backend chat session

    // Create AI message for streaming
    const aiMessageId = generateMessageId();
    const aiMessage: ChatMessage = {
      id: aiMessageId,
      content: '',
      sender: 'ai',
      timestamp: new Date(),
      isStreaming: true
    };
    setChatHistory(prev => [...prev, aiMessage]);
    try {
      let finalContent = '';
      let comparisonCitations: any[] = [];
      
      // Use Smart AI service for policy comparisons with citations
      try {
        const smartResponse = await fetch(`${API_BASE_URL}/api/comparison/generate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            documentIds: selectedDocuments.map(doc => doc.id),
            documents: selectedDocuments.map(doc => ({
              id: doc.id,
              name: doc.name,
              policyType: doc.policyType,
              extractedText: doc.extractedText || ''
            })),
            options: {
              smartCitations: true,
              enhanceCitations: true,
              validateCitations: true
            }
          })
        });
        
        if (smartResponse.ok) {
          const smartResult = await smartResponse.json();
          finalContent = smartResult.report || '';
          comparisonCitations = smartResult.citations || [];
          devLog(`📊 Home comparison received ${comparisonCitations.length} citations from Smart AI`);
          
          // Update streaming display
          setChatHistory((prev: ChatMessage[]) => 
            prev.map(msg => msg.id === aiMessageId ? { ...msg, content: finalContent } : msg)
          );
        } else {
          throw new Error('Smart comparison service failed');
        }
      } catch (smartError) {
        console.warn('Smart comparison service failed, falling back to basic AI:', smartError);
        // Fallback to basic AI service
        await aiService.sendRawPrompt(fullPrompt, (streamContent) => {
          finalContent = streamContent;
          setChatHistory((prev: ChatMessage[]) => 
            prev.map(msg => msg.id === aiMessageId ? { ...msg, content: streamContent } : msg)
          );
        });
      }
      
      // Finalize AI message with citations
      const completedMessage: ChatMessage = { 
        ...aiMessage, 
        content: finalContent, 
        isStreaming: false,
        citations: comparisonCitations
      };
      setChatHistory((prev: ChatMessage[]) => prev.map(msg => msg.id === aiMessageId ? completedMessage : msg));
      // Save only the AI response to the backend, not the system-generated title message
      await addChatMessage(completedMessage, `Compare Policies: ${docNames}`);
    } catch (error) {
      console.error('Compare error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // API functions for customer data
  const fetchCustomerDocuments = async (customerId: string) => {
    try {
      devLog('📄 Fetching documents for customer:', customerId);
      const response = await fetch(`${API_BASE_URL}/api/customers/${customerId}/documents`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const docs = await response.json();
        devLog('📄 Fetched', docs.length, 'documents for customer:', customerId);
        devLog('📄 Document details:', docs.map((d: any) => ({
          id: d.id,
          name: d.name,
          status: d.status,
          hasExtractedText: !!d.extractedText,
          extractedTextLength: d.extractedText?.length || 0,
          extractedTextPreview: d.extractedText ? d.extractedText.substring(0, 100) + '...' : 'No text available'
        })));
        
        // Debug: Check raw document data
        if (docs.length > 0) {
          devLog('📄 Raw first document keys:', Object.keys(docs[0]));
          devLog('📄 Raw first document:', docs[0]);
        }
        setCustomerDocuments(prev => ({ ...prev, [customerId]: docs }));
        return docs;
      } else {
        console.error('Failed to fetch customer documents:', response.status, await response.text());
      }
    } catch (error) {
      console.error('Error fetching customer documents:', error);
    }
    return [];
  };

  const fetchCustomerChats = async (customerId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/customers/${customerId}/chats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const chats = await response.json();
        setCustomerChats(prev => ({ ...prev, [customerId]: chats }));
        return chats;
      }
    } catch (error) {
      console.error('Error fetching customer chats:', error);
    }
    return [];
  };

  const fetchCustomerReports = async (customerId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/customers/${customerId}/reports`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const reports = await response.json();
        setCustomerReports(prev => ({ ...prev, [customerId]: reports }));
        return reports;
      }
    } catch (error) {
      console.error('Error fetching customer reports:', error);
    }
    return [];
  };

  // Navigate to WorkflowReport with chat session
  const navigateToWorkflowReport = (chat: any) => {
    devLog('Navigating to WorkflowReport with chat:', chat.id);
    
    // Build URL parameters
    const params = new URLSearchParams();
    params.append('sessionId', chat.id);
    
    if (selectedCustomer?.id) {
      params.append('customerId', selectedCustomer.id);
    }
    
    if (chat.projectId || selectedProject?.id) {
      params.append('projectId', chat.projectId || selectedProject?.id || '');
    }
    
    if (chat.reportId) {
      params.append('reportId', chat.reportId);
    }
    
    const url = `/workflow-report?${params.toString()}`;
    devLog('Navigating to:', url);
    navigate(url);
  };

  const loadChatSession = async (sessionId: string) => {
    devLog('Loading chat session:', sessionId);
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/chat/session/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
        }
      });
      
      // Handle both 200 and 304 status codes as successful
      if (response.ok || response.status === 304) {
        let session;
        if (response.status === 304) {
          // If 304, we need to make a fresh request to get the data
          const freshResponse = await fetch(`${API_BASE_URL}/api/chat/session/${sessionId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`,
              'Cache-Control': 'no-cache'
            }
          });
          session = await freshResponse.json();
        } else {
          session = await response.json();
        }
        devLog('Loaded session:', session);
        
        // If the session has a customer and it's different from current, select it
        if (session.customer && (!selectedCustomer || selectedCustomer.id !== session.customer.id)) {
          setSelectedCustomer(session.customer);
          setCurrentCustomer(session.customer.id);
        }
        
        // Load the chat history with correct ChatMessage format
        const messages = session.messages.map((msg: any) => ({
          id: msg.id,
          content: msg.content,
          sender: msg.sender,
          timestamp: new Date(msg.timestamp),
          isStreaming: false
        }));
        
        setChatHistory(messages);
        
        // Set the chat title from the loaded session
        setChatTitle(session.title || 'Untitled Chat');
        setIsEditingTitle(false);
        
        // Restore document context from the session if available
        // Check if any messages have document context and restore those documents
        const messagesWithContext = session.messages.filter((msg: any) => msg.context?.documentNames?.length > 0);
        if (messagesWithContext.length > 0) {
          const lastContextMessage = messagesWithContext[messagesWithContext.length - 1];
          if (lastContextMessage.context.documentNames) {
            devLog('Restoring document context for session:', lastContextMessage.context.documentNames);
            // Try to match and select documents that were used in this session
            const availableDocuments = getCustomerDocuments(session.customer?.id || selectedCustomer?.id || '');
            const documentsToSelect = availableDocuments.filter(doc => 
              lastContextMessage.context.documentNames.includes(doc.name)
            );
            if (documentsToSelect.length > 0) {
              setSelectedDocumentIds(documentsToSelect.map(doc => doc.id));
              devLog('Restored document selection:', documentsToSelect.map(doc => doc.name));
            }
            
            // Restore policy type if available
            if (lastContextMessage.context.policyType) {
              setSelectedPolicyType(lastContextMessage.context.policyType);
              devLog('Restored policy type:', lastContextMessage.context.policyType);
            }
          }
        }
        
        // Set the current chat session in DocumentContext LAST to ensure it doesn't get cleared
        setCurrentChatSession(session.id);
        
        return session;
      } else {
        console.error('Failed to load chat session:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error loading chat session:', error);
    } finally {
      setIsLoading(false);
    }
    return null;
  };

  // Updated customer data functions
  const getCustomerDocuments = (customerId: string) => {
    return customerDocuments[customerId] || [];
  };

  const getCustomerChats = (customerId: string) => {
    return customerChats[customerId] || [];
  };

  const getCustomerReports = (customerId: string) => {
    return customerReports[customerId] || [];
  };

  // Project-filtered functions for when in project-details mode
  const getProjectReports = (projectId: string) => {
    // Use projectReports when viewing project details (project-specific reports)
    // Use comparisonReports filtered by projectId when viewing customer details (fallback)
    if (currentNavigationView === 'project-details' && projectReports.length > 0) {
      devLog('📊 Using project-specific reports for', projectId, ':', projectReports.length, 'reports');
      return projectReports;
    }
    
    const filtered = comparisonReports.filter(report => report.projectId === projectId);
    devLog('📊 Project reports for', projectId, ':', filtered.length, 'out of', comparisonReports.length, 'total reports');
    devLog('📊 All reports:', comparisonReports.map(r => ({ id: r.id, title: r.title, projectId: r.projectId })));
    devLog('📊 Filtered reports:', filtered.map(r => ({ id: r.id, title: r.title, projectId: r.projectId })));
    return filtered;
  };

  const getProjectChats = (projectId: string) => {
    // Filter all chat sessions that belong to this project
    const allChats = Object.values(customerChats).flat();
    const filtered = allChats.filter(chat => chat.projectId === projectId);
    devLog('💬 Project chats for', projectId, ':', filtered.length, 'out of', allChats.length, 'total chats');
    return filtered;
  };

  // Pagination helpers for chat history tiles
  const getPaginatedChats = (customerId: string) => {
    const chats = getCustomerChats(customerId);
    const startIndex = chatHistoryPage * CHATS_PER_PAGE;
    const endIndex = startIndex + CHATS_PER_PAGE;
    return chats.slice(startIndex, endIndex);
  };

  const getPaginatedProjectChats = (projectId: string) => {
    const chats = getProjectChats(projectId);
    const startIndex = chatHistoryPage * CHATS_PER_PAGE;
    const endIndex = startIndex + CHATS_PER_PAGE;
    return chats.slice(startIndex, endIndex);
  };

  const getTotalChatPages = (customerId: string) => {
    const chats = getCustomerChats(customerId);
    return Math.ceil(chats.length / CHATS_PER_PAGE);
  };

  const getTotalProjectChatPages = (projectId: string) => {
    const chats = getProjectChats(projectId);
    return Math.ceil(chats.length / CHATS_PER_PAGE);
  };

  const handleNextChatPage = () => {
    if (selectedCustomer) {
      const totalPages = currentNavigationView === 'project-details' && selectedProject
        ? getTotalProjectChatPages(selectedProject.id)
        : getTotalChatPages(selectedCustomer.id);
      if (chatHistoryPage < totalPages - 1) {
        setChatHistoryPage(prev => prev + 1);
      }
    }
  };

  const handlePrevChatPage = () => {
    if (chatHistoryPage > 0) {
      setChatHistoryPage(prev => prev - 1);
    }
  };

  // Pagination helpers for reports
  const getPaginatedReports = (customerId: string) => {
    const reports = getCustomerReports(customerId);
    const startIndex = reportsPage * REPORTS_PER_PAGE;
    const endIndex = startIndex + REPORTS_PER_PAGE;
    return reports.slice(startIndex, endIndex);
  };

  const getTotalReportsPages = (customerId: string) => {
    const reports = getCustomerReports(customerId);
    return Math.ceil(reports.length / REPORTS_PER_PAGE);
  };

  // Project-filtered pagination functions
  const getPaginatedProjectReports = (projectId: string) => {
    const reports = getProjectReports(projectId);
    const startIndex = reportsPage * REPORTS_PER_PAGE;
    const endIndex = startIndex + REPORTS_PER_PAGE;
    return reports.slice(startIndex, endIndex);
  };

  const getTotalProjectReportsPages = (projectId: string) => {
    const reports = getProjectReports(projectId);
    return Math.ceil(reports.length / REPORTS_PER_PAGE);
  };

  const handleNextReportsPage = () => {
    if (selectedCustomer) {
      const isProjectView = currentNavigationView === 'project-details' && selectedProject;
      const totalPages = isProjectView
        ? getTotalProjectReportsPages(selectedProject.id)
        : getTotalReportsPages(selectedCustomer.id);
      
      devLog('📄 Next Reports Page clicked:', { 
        isProjectView, 
        currentPage: reportsPage, 
        totalPages,
        selectedProject: selectedProject?.id,
        selectedCustomer: selectedCustomer.id 
      });
      
      if (reportsPage < totalPages - 1) {
        setReportsPage(prev => {
          const newPage = prev + 1;
          devLog('📄 Reports page changed from', prev, 'to', newPage);
          return newPage;
        });
      }
    }
  };

  const handlePrevReportsPage = () => {
    if (reportsPage > 0) {
      setReportsPage(prev => prev - 1);
    }
  };

  // Pagination helpers for documents
  const getPaginatedDocuments = (customerId: string) => {
    const docs = getCustomerDocuments(customerId);
    const startIndex = documentsPage * DOCUMENTS_PER_PAGE;
    const endIndex = startIndex + DOCUMENTS_PER_PAGE;
    return docs.slice(startIndex, endIndex);
  };

  const getPaginatedProjectDocuments = () => {
    const startIndex = documentsPage * DOCUMENTS_PER_PAGE;
    const endIndex = startIndex + DOCUMENTS_PER_PAGE;
    return documents.slice(startIndex, endIndex);
  };

  const getTotalDocumentsPages = (customerId: string) => {
    const docs = getCustomerDocuments(customerId);
    return Math.ceil(docs.length / DOCUMENTS_PER_PAGE);
  };

  const getTotalProjectDocumentsPages = () => {
    return Math.ceil(documents.length / DOCUMENTS_PER_PAGE);
  };

  const handleNextDocumentsPage = () => {
    if (selectedCustomer) {
      const totalPages = currentNavigationView === 'project-details' && selectedProject
        ? getTotalProjectDocumentsPages()
        : getTotalDocumentsPages(selectedCustomer.id);
      if (documentsPage < totalPages - 1) {
        setDocumentsPage(prev => prev + 1);
      }
    }
  };

  const handlePrevDocumentsPage = () => {
    if (documentsPage > 0) {
      setDocumentsPage(prev => prev - 1);
    }
  };

  // Helper function to reset pagination when switching views
  const handleDetailViewChange = (view: 'workflows' | 'chats' | 'documents' | 'reports' | 'proposals' | 'insurance_summary') => {
    setCustomerDetailView(view);
    // Reset pagination for all views
    setChatHistoryPage(0);
    setReportsPage(0);
    setDocumentsPage(0);
  };

  const handleClearChat = () => {
    setChatHistory([]);
    startNewSession();
    if (selectedCustomer) {
      const today = new Date().toLocaleDateString();
      setChatTitle(`${selectedCustomer.name} - ${today}`);
    } else {
      setChatTitle('');
    }
    setIsEditingTitle(false);
  };

  const handleTitleEdit = () => {
    setIsEditingTitle(true);
  };

  const handleTitleSave = async () => {
    setIsEditingTitle(false);
    
    // Note: Chat titles are now auto-generated by AI in workflow chat
    // Manual title editing functionality disabled for now
    devLog('Chat title editing disabled - titles are auto-generated:', chatTitle);
  };

  const handleTitleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setIsEditingTitle(false);
    }
  };

  const handleDeleteDocument = async (documentId: string, documentName: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent any parent click handlers
    
    if (!window.confirm(`Are you sure you want to delete "${documentName}"? This cannot be undone.`)) {
      return;
    }

    try {
      devLog('🗑️ Deleting document:', documentId, documentName);
      
      // First, optimistically update the UI by removing from customer documents
      if (selectedCustomer) {
        setCustomerDocuments(prev => ({
          ...prev,
          [selectedCustomer.id]: prev[selectedCustomer.id]?.filter(doc => doc.id !== documentId) || []
        }));
      }
      
      // Call the backend to delete the document
      const response = await fetch(`${API_BASE_URL}/api/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        // Update the global documents state directly (don't call removeDocument as it makes another API call)
        setDocuments(prev => prev.filter(doc => doc.id !== documentId));
        setSelectedDocumentIds(prev => prev.filter(id => id !== documentId));
        devLog('✅ Document deleted successfully');
      } else {
        // If backend deletion failed, revert the optimistic update
        if (selectedCustomer) {
          await fetchCustomerDocuments(selectedCustomer.id);
        }
        throw new Error(`Backend deletion failed: ${response.status}`);
      }
      
    } catch (error) {
      console.error('❌ Error deleting document:', error);
      alert('Failed to delete document. Please try again.');
      
      // Refresh customer documents to restore correct state
      if (selectedCustomer) {
        await fetchCustomerDocuments(selectedCustomer.id);
      }
    }
  };

  const handleDeleteChatSession = async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent the click from triggering loadChatSession
    
    if (!window.confirm('Are you sure you want to delete this chat conversation? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/session/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`,
        },
      });

      if (response.ok) {
        // Refresh the customer chats
        if (selectedCustomer) {
          await fetchCustomerChats(selectedCustomer.id);
        }
      } else {
        console.error('Failed to delete chat session');
        alert('Failed to delete chat session');
      }
    } catch (error) {
      console.error('Error deleting chat session:', error);
      alert('Error deleting chat session');
    }
  };

  const handleDeleteReport = async (reportId: string, reportTitle: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent the click from triggering handleReportClick
    
    if (!window.confirm(`Are you sure you want to delete "${reportTitle}"? This cannot be undone.`)) {
      return;
    }

    // Prevent double-clicks by checking if element is already disabled
    const target = e.currentTarget as HTMLButtonElement;
    if (target.disabled) {
      return;
    }
    target.disabled = true;

    try {
      devLog('🗑️ Deleting report:', reportId, reportTitle);
      
      // Check if selectedCustomer exists
      if (!selectedCustomer) {
        console.error('No customer selected for report deletion');
        return;
      }
      
      // Optimistically update the UI - check if we're in project view or customer view
      if (currentNavigationView === 'project-details' && selectedProject) {
        // Update project reports
        setProjectReports(prev => prev.filter(report => report.id !== reportId));
      } else {
        // Update customer reports
        setCustomerReports(prev => ({
          ...prev,
          [selectedCustomer.id]: prev[selectedCustomer.id]?.filter(report => report.id !== reportId) || []
        }));
      }
      
      // Call the backend to delete the report using unified endpoint
      const response = await fetch(`${API_BASE_URL}/api/customers/${selectedCustomer.id}/reports/${reportId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        // Remove from selected reports if it was selected
        setCustomerSelectedReportIds(prev => prev.filter(id => id !== reportId));
        devLog('✅ Report deleted successfully');
      } else {
        // If backend deletion failed, revert the optimistic update
        if (currentNavigationView === 'project-details' && selectedProject) {
          await fetchProjectReports(selectedProject.id);
        } else if (selectedCustomer) {
          await fetchCustomerReports(selectedCustomer.id);
        }
        throw new Error(`Backend deletion failed: ${response.status}`);
      }
      
    } catch (error) {
      console.error('❌ Error deleting report:', error);
      alert('Failed to delete report. Please try again.');
      
      // Refresh reports to restore correct state
      if (currentNavigationView === 'project-details' && selectedProject) {
        await fetchProjectReports(selectedProject.id);
      } else if (selectedCustomer) {
        await fetchCustomerReports(selectedCustomer.id);
      }
    } finally {
      // Re-enable the button
      target.disabled = false;
    }
  };

  // Handle citation clicks: use HTML PDF viewer for citations
  const handleCitationClick = async (citation: any) => {
    // Citation functionality removed - this function does nothing
    devLog('Citation functionality has been disabled');
  };

  // Removed old citation handling code

  // Document selection handlers
  const handleDocumentCheckboxChange = (documentId: string, checked: boolean) => {
    let newSelectedIds: string[];
    if (checked) {
      newSelectedIds = [...customerSelectedDocumentIds, documentId];
      setCustomerSelectedDocumentIds(newSelectedIds);
    } else {
      newSelectedIds = customerSelectedDocumentIds.filter(id => id !== documentId);
      setCustomerSelectedDocumentIds(newSelectedIds);
    }
    
    // Update the global chat context with selected documents
    setSelectedDocumentIds(newSelectedIds);
    devLog('📄 Updated chat context with selected documents:', newSelectedIds);
  };

  // Report selection handlers
  const handleReportCheckboxChange = (reportId: string, checked: boolean) => {
    if (checked) {
      setCustomerSelectedReportIds(prev => [...prev, reportId]);
    } else {
      setCustomerSelectedReportIds(prev => prev.filter(id => id !== reportId));
    }
  };

  const handleReportClick = async (report: UnderwritingReport | ComparisonReport) => {
    devLog('📄 Opening report:', report.title);
    
    // Check if this is a PDF report that should open in PDF viewer
    if ('additionalFacts' in report && report.additionalFacts) {
      try {
        const metadata = JSON.parse(report.additionalFacts);
        if (metadata.openAs === 'pdf_viewer' || metadata.isPdfReport === true) {
          let pdfUrl = metadata.pdfUrl;
          
          // If no pdfUrl in metadata, try to extract from report content
          if (!pdfUrl && 'content' in report && report.content) {
            try {
              const content = JSON.parse(report.content);
              pdfUrl = content.downloadUrl || content.pdfUrl;
              devLog('📄 No pdfUrl in metadata, found in content:', pdfUrl ? 'Yes' : 'No');
            } catch (e) {
              devLog('📄 Could not parse report content for PDF URL');
            }
          }
          
          if (pdfUrl) {
            devLog('📄 Opening PDF report in viewer. URL type:', pdfUrl.startsWith('blob:') ? 'blob URL (may be expired)' : pdfUrl.startsWith('data:') ? 'base64 data URL' : 'other URL');
            devLog('📄 PDF URL preview:', pdfUrl.substring(0, 100) + '...');
            // Create a document-like object for the PDF viewer
            const pdfDocument: PolicyDocument = {
              id: report.id,
              name: report.title,
              size: 0,
              type: 'application/pdf',
              progress: 100,
              status: 'completed',
              uploadedAt: new Date(report.createdAt),
              fileUrl: pdfUrl,
              file: undefined
            };
            setViewingDocument(pdfDocument);
            setShowDocumentViewer(true);
            return;
          }
        }
      } catch (e) {
        devLog('📄 Not a PDF report or metadata parsing failed');
      }
    }
    
    let documents: any[] = [];
    let hideCitations = true;
    
    // Load documents if the report has document metadata
    if ('documentIds' in report && report.documentIds && report.documentIds.length > 0) {
      devLog('📄 Loading documents for report:', report.documentIds);
      
      try {
        const token = localStorage.getItem('riskninja-token');
        if (token) {
          const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
          const response = await fetch(`${apiUrl}/api/documents/load-by-ids`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              documentIds: report.documentIds
            })
          });
          
          if (response.ok) {
            const result = await response.json();
            if (result.success && result.documents) {
              documents = result.documents;
              hideCitations = false; // Enable citations when documents are available
              devLog(`📄 Successfully loaded ${documents.length} documents for report viewing`);
            }
          } else {
            devWarn('📄 Failed to load documents for report:', response.status);
          }
        }
      } catch (error) {
        console.error('📄 Error loading documents for report:', error);
      }
    }
    
    // Navigate to WorkflowReport page with report data and loaded documents
    navigate('/workflow-report', {
      state: {
        content: report.content,
        workflowType: ('workflowType' in report && report.workflowType) ? report.workflowType : 'existing_report',
        documents: documents, // Now includes loaded document content
        customerId: selectedCustomer?.id,
        customerName: selectedCustomer ? getCustomerDisplayName(selectedCustomer) : undefined,
        projectId: ('projectId' in report && report.projectId) ? report.projectId : (currentNavigationView === 'project-details' ? selectedProject?.id : undefined),
        reportId: report.id,
        reportTitle: report.title,
        createdAt: report.createdAt,
        updatedAt: ('updatedAt' in report) ? report.updatedAt : report.createdAt,
        hideCitations: hideCitations, // Enable citations if documents were loaded
        // Include original document metadata for regeneration
        documentNames: ('documentNames' in report) ? report.documentNames : [],
        documentIds: ('documentIds' in report) ? report.documentIds : []
      }
    });
  };

  const handleDocumentClick = async (document: PolicyDocument) => {
    devLog('📄 Opening document viewer for:', document.name, document);
    
    let documentToView = document;
    
    // If the document has an ID but no local file, use the streaming endpoint
    if (document.id && !document.file) {
      // Use the streaming endpoint URL which works through the API
      const streamUrl = `${API_BASE_URL}/api/documents/${document.id}/stream`;
      devLog('📄 Using document stream URL:', streamUrl);
      documentToView = { ...document, fileUrl: streamUrl };
    }
    
    setViewingDocument(documentToView);
    setShowDocumentViewer(true);
  };

  const getSelectedCustomerDocuments = () => {
    if (!selectedCustomer) return [];
    const customerDocs = getCustomerDocuments(selectedCustomer.id);
    return customerDocs.filter(doc => customerSelectedDocumentIds.includes(doc.id));
  };

  const getSelectedCustomerReports = () => {
    if (!selectedCustomer) return [];
    const customerReports = getCustomerReports(selectedCustomer.id);
    return customerReports.filter(report => customerSelectedReportIds.includes(report.id));
  };

  // Project-specific selection functions for project-details view
  const getSelectedProjectDocuments = () => {
    if (!selectedProject) return [];
    return documents.filter(doc => customerSelectedDocumentIds.includes(doc.id));
  };

  const getSelectedProjectReports = () => {
    if (!selectedProject) return [];
    const projectReports = getProjectReports(selectedProject.id);
    return projectReports.filter(report => customerSelectedReportIds.includes(report.id));
  };

  const handleGenerateUnderwritingReport = async () => {
    if (!selectedCustomer) return;
    
    // Get customer documents for context
    const customerDocs = getCustomerDocuments(selectedCustomer.id);
    const selectedDocs = getSelectedCustomerDocuments();
    
    // Use selected documents if any, otherwise use all customer documents
    const docsToUse = selectedDocs.length > 0 ? selectedDocs : customerDocs;
    
    if (docsToUse.length === 0) {
      alert('No documents available for this customer. Please upload documents first.');
      return;
    }

    // Set the selected documents in the global context for the research page
    setSelectedDocumentIds(docsToUse.map(doc => doc.id));
    
    // Store customer context in localStorage for the research page
    const customerContext = {
      customerId: selectedCustomer.id,
      customerName: getCustomerDisplayName(selectedCustomer),
      company: selectedCustomer.company || '',
      type: selectedCustomer.type,
      email: selectedCustomer.email || '',
      phone: selectedCustomer.phone || '',
      documents: docsToUse.map(doc => ({ id: doc.id, name: doc.name }))
    };
    
    localStorage.setItem('riskninja-customer-context', JSON.stringify(customerContext));
    
    // Navigate to research page
    window.location.href = '/research';
  };

  // Updated chat handler to use smart citations system
  const handleCustomerChatMessage = async (message: string) => {
    if (!selectedCustomer) return;

    // Add user message
    const userMessage: ChatMessage = {
      id: generateMessageId(),
      content: message,
      sender: 'user',
      timestamp: new Date()
    };
    
    setChatHistory(prev => [...prev, userMessage]);

    // Get selected documents and reports for context
    const selectedDocs = getSelectedCustomerDocuments();
    const selectedReports = getSelectedCustomerReports();
    
    // Fetch full document data with extractedText if needed
    const docsWithText = await Promise.all(
      selectedDocs.map(async (doc) => {
        try {
          // If document already has extractedText, use it
          if (doc.extractedText) {
            return doc;
          }
          
          // Otherwise, fetch the full document data
          const response = await fetch(`${API_BASE_URL}/api/documents/${doc.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            const fullDoc = await response.json();
            devLog(`📄 Fetched full document ${doc.name} for customer chat:`, {
              hasExtractedText: !!fullDoc.extractedText,
              extractedTextLength: fullDoc.extractedText ? fullDoc.extractedText.length : 0
            });
            return { ...doc, extractedText: fullDoc.extractedText || '' };
          } else {
            console.error(`Failed to fetch full document ${doc.id} for customer chat`);
            return doc;
          }
        } catch (error) {
          console.error(`Error fetching document ${doc.id} for customer chat:`, error);
          return doc;
        }
      })
    );
    
    // Create or reuse session and send user message (similar to handleSendMessage)
    const sessionId = await addChatMessage(userMessage, !currentChatSessionId ? (chatTitle.trim() || undefined) : undefined);
    setIsLoading(true);

    // Create AI message with streaming
    const aiMessageId = generateMessageId();
    const aiMessage: ChatMessage = {
      id: aiMessageId,
      content: '',
      sender: 'ai',
      timestamp: new Date(),
      isStreaming: true
    };
    
    setChatHistory(prev => [...prev, aiMessage]);

    try {
      let finalAiContent = '';
      let responseCitations: any[] = [];
      
      if (docsWithText.length > 0 || selectedReports.length > 0) {
        // Use Smart AI service for document/report-based chat with citations
        try {
          devLog('📄 Customer chat - Selected documents:', docsWithText.length);
          devLog('📄 Customer chat - Documents being sent:', docsWithText.map((d: any) => ({
            id: d.id,
            name: d.name,
            status: d.status,
            hasExtractedText: !!d.extractedText,
            textLength: (d.extractedText || '').length,
            preview: (d.extractedText || '').substring(0, 100) + '...' || 'No text available'
          })));

          // TROUBLESHOOTING: Check if any documents have empty extractedText
          const docsWithoutText = docsWithText.filter((d: any) => !d.extractedText || d.extractedText.trim().length === 0);
          if (docsWithoutText.length > 0) {
            console.error('🚨 CRITICAL: Documents without extracted text found:', docsWithoutText.map((d: any) => ({
              id: d.id,
              name: d.name,
              status: d.status,
              hasExtractedText: !!d.extractedText,
              extractedTextLength: (d.extractedText || '').length
            })));
            console.error('❌ These documents will be filtered out by the backend and won\'t be sent to AI');
          }
          
          // Check if any documents are still processing
          const processingDocs = docsWithText.filter((d: any) => d.status !== 'completed');
          if (processingDocs.length > 0) {
            devWarn('⚠️ Some selected documents are not fully processed:', processingDocs.map((d: any) => ({
              name: d.name,
              status: d.status
            })));
          }

          // ENHANCED DEBUGGING: Log exactly what we're sending to the backend
          const requestBody = {
            message: message,
            documents: docsWithText.map(doc => ({
              id: doc.id,
              name: doc.name,
              extractedText: doc.extractedText || ''
            })),
            reportContent: selectedReports.length > 0 ? selectedReports.map(report => report.content).join('\n\n---\n\n') : undefined,
            reportMetadata: selectedReports.length > 0 ? {
              workflowType: 'customer_analysis',
              customerName: getCustomerDisplayName(selectedCustomer),
              customerId: selectedCustomer.id,
              reports: selectedReports.map(report => ({
                id: report.id,
                title: report.title
              }))
            } : undefined,
            sessionId: sessionId,
            options: {
              smartCitations: true,
              enhanceCitations: true,
              validateCitations: true
            }
          };

          devLog('🚀 CUSTOMER CHAT REQUEST BODY BEING SENT:', JSON.stringify({
            ...requestBody,
            documents: requestBody.documents.map(d => ({
              id: d.id,
              name: d.name,
              extractedTextLength: d.extractedText.length,
              extractedTextPreview: d.extractedText.substring(0, 200) + '...'
            }))
          }, null, 2));
          
          const smartResponse = await fetch(`${API_BASE_URL}/api/chat/smart`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(requestBody)
          });
          
          if (smartResponse.ok) {
            const smartResult = await smartResponse.json();
            finalAiContent = smartResult.response || smartResult.message || '';
            responseCitations = smartResult.citations || [];
            
            devLog('✅ BACKEND RESPONSE:', {
              responseLength: finalAiContent.length,
              citationsCount: responseCitations.length,
              documentsUsed: smartResult.documentsUsed || [],
              responsePreview: finalAiContent.substring(0, 200) + '...'
            });
            
            // Check if AI response seems document-aware
            if (requestBody.documents.length > 0 && finalAiContent.length > 0) {
              const hasDocumentReferences = /document|policy|according to|based on|shows that|indicates/i.test(finalAiContent);
              if (!hasDocumentReferences) {
                devWarn('⚠️ AI response does not seem to reference document content. Possible issues:');
                console.warn('   - Documents may not have extracted text');
                console.warn('   - Backend may be filtering out documents');
                console.warn('   - AI may not be processing document content properly');
              } else {
                devLog('✅ AI response appears to reference document content');
              }
            }
            
            devLog(`📊 Customer chat received ${responseCitations.length} citations from Smart AI`);
            
            // Update streaming display
            setChatHistory(prev =>
              prev.map(msg =>
                msg.id === aiMessageId ? { ...msg, content: finalAiContent } : msg
              )
            );
          } else {
            const errorText = await smartResponse.text();
            console.error('❌ BACKEND ERROR RESPONSE:', errorText);
            throw new Error(`Smart chat failed: ${smartResponse.status} - ${errorText}`);
          }
        } catch (smartError) {
          console.warn('Smart chat failed, falling back to basic AI:', smartError);
          // Fallback to basic AI service
          await aiService.sendRawPrompt(message, (streamContent) => {
            finalAiContent = streamContent;
            setChatHistory(prev =>
              prev.map(msg =>
                msg.id === aiMessageId ? { ...msg, content: streamContent } : msg
              )
            );
          });
        }
      } else {
        // No documents or reports selected, use basic AI service
        await aiService.sendRawPrompt(`You are helping with customer "${selectedCustomer.name}". ${message}`, (streamContent) => {
          finalAiContent = streamContent;
          setChatHistory(prev =>
            prev.map(msg =>
              msg.id === aiMessageId ? { ...msg, content: streamContent } : msg
            )
          );
        });
      }

      // Mark streaming as complete and save message
      const finalAiMessage: ChatMessage = {
        id: aiMessageId,
        content: finalAiContent,
        sender: 'ai',
        timestamp: new Date(),
        isStreaming: false,
        citations: responseCitations
      };

      setChatHistory(prev => 
        prev.map(msg => 
          msg.id === aiMessageId 
            ? finalAiMessage
            : msg
        )
      );

      // Save AI message to backend
      await addChatMessage(finalAiMessage, undefined, sessionId || undefined);

      // Update citation map if we have citations
      if (responseCitations.length > 0) {
        const newCitationSpans = responseCitations
          .filter(citation => citation.docId && citation.text)
          .map(citation => ({
            docId: citation.docId,
            pageNumber: citation.page || 1,
            startOffset: citation.startOffset || 0,
            endOffset: citation.endOffset || 0,
            text: citation.text || citation.display || ''
          }));
        
        setCitationMap([...citationMap, ...newCitationSpans]);
      }

    } catch (error) {
      console.error('Customer chat error:', error);
      const errorMessage: ChatMessage = {
        id: aiMessageId,
        content: 'Sorry, I encountered an error. Please try again.',
        sender: 'ai',
        timestamp: new Date(),
        isStreaming: false
      };

      setChatHistory(prev => 
        prev.map(msg => 
          msg.id === aiMessageId 
            ? errorMessage
            : msg
        )
      );

      // Save error message to backend
      await addChatMessage(errorMessage, undefined, sessionId || undefined);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col p-6 max-w-7xl mx-auto overflow-y-auto">
      {/* Navigation-based Content Section */}
      <div className="mb-8">
        <div className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg p-6">
          {(currentNavigationView !== 'customer-selection' && !selectedCustomer) ||
           (currentNavigationView === 'project-details' && !selectedProject) ? (
            // Deep link / refresh landed directly on a project-selection or
            // project-details URL — currentNavigationView is derived from the
            // URL synchronously, but selectedCustomer/selectedProject load
            // asynchronously (and can fail, e.g. a dropped fetch mid-load).
            // Show a loading state rather than rendering
            // ProjectManagement/ProjectDetailView with a null customer/project.
            <div className="flex items-center justify-center py-24">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
            </div>
          ) : currentNavigationView === 'customer-selection' ? (
            <CustomerSelectionView
              selectedView={selectedView}
              setSelectedView={setSelectedView}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
              filters={customerFilters}
              setFilters={setCustomerFilters}
              loadingCustomers={loadingCustomers}
              isAms360Enabled={isAms360Enabled}
              currentList={getCurrentList()}
              onSelectCustomer={handleSelectCustomer}
              onSelectReport={handleSelectReport}
              onConvertToCustomer={handleConvertToCustomer}
              onEditCustomer={handleEditCustomer}
              onDeleteCustomer={handleDeleteCustomer}
              onShareCustomer={handleShareCustomer}
              onTransferCustomer={handleTransferCustomer}
              onOpenChat={(c) => setChatCustomer(c)}
              onAddNew={(type) => {
                setNewCustomerType(type);
                setNewCustomerForm({ name: '', email: '', phone: '', company: '', status: 'active', lineType: 'commercial', secondaryContact: '' });
                setShowNewCustomerModal(true);
              }}
              onOpenAMS360Import={() => setShowAMS360ImportModal(true)}
              onOpenGroupManagement={() => setShowGroupModal(true)}
              onShowAuditLog={(c) => { setAuditCustomerId(c.id); setAuditCustomerName(getCustomerDisplayName(c)); }}
              onRefreshAfterBulk={fetchCustomers}
            />
          ) : currentNavigationView === 'project-selection' ? (
            /* Project Selection View */
            <ProjectManagement
              customerId={selectedCustomer!.id}
              customerName={getCustomerDisplayName(selectedCustomer!)}
              onProjectSelect={handleSelectProject}
              onBack={handleBackToCustomers}
              ams360CustomerId={selectedCustomer!.ams360CustomerId}
              isAms360Enabled={isAms360Enabled}
            />
          ) : (
            /* Project Detail View */
            <ProjectDetailView
              selectedCustomer={selectedCustomer!}
              selectedProject={selectedProject}
              customerDetailView={customerDetailView}
              currentNavigationView={currentNavigationView}
              documents={documents}
              customerSelectedDocumentIds={customerSelectedDocumentIds}
              customerSelectedReportIds={customerSelectedReportIds}
              editingReportId={editingReportId}
              editingReportTitle={editingReportTitle}
              chatHistoryPage={chatHistoryPage}
              reportsPage={reportsPage}
              documentsPage={documentsPage}
              getCustomerDocuments={getCustomerDocuments}
              getCustomerChats={getCustomerChats}
              getCustomerReports={getCustomerReports}
              getProjectChats={getProjectChats}
              getProjectReports={getProjectReports}
              getPaginatedChats={getPaginatedChats}
              getPaginatedProjectChats={getPaginatedProjectChats}
              getPaginatedReports={getPaginatedReports}
              getPaginatedProjectReports={getPaginatedProjectReports}
              getPaginatedDocuments={getPaginatedDocuments}
              getPaginatedProjectDocuments={getPaginatedProjectDocuments}
              getTotalChatPages={getTotalChatPages}
              getTotalProjectChatPages={getTotalProjectChatPages}
              getTotalReportsPages={getTotalReportsPages}
              getTotalProjectReportsPages={getTotalProjectReportsPages}
              getTotalDocumentsPages={getTotalDocumentsPages}
              getTotalProjectDocumentsPages={getTotalProjectDocumentsPages}
              onBackToProjects={handleBackToProjects}
              onConvertToCustomer={handleConvertToCustomer}
              onDetailViewChange={handleDetailViewChange}
              onShowContactOverlay={() => setShowContactOverlay(true)}
              onNavigateToWorkflowReport={navigateToWorkflowReport}
              onDeleteChatSession={handleDeleteChatSession}
              onDocumentClick={handleDocumentClick}
              onDocumentCheckboxChange={handleDocumentCheckboxChange}
              onReportCheckboxChange={handleReportCheckboxChange}
              onDeleteDocument={handleDeleteDocument}
              onReportClick={handleReportClick}
              onEditReportTitle={handleEditReportTitle}
              onSaveReportTitle={handleSaveReportTitle}
              onDeleteReport={handleDeleteReport}
              setEditingReportTitle={setEditingReportTitle}
              onShowUploader={() => setShowUploader(true)}
              onWorkflowComplete={async () => {
                if (currentNavigationView === 'project-details' && selectedProject?.id) {
                  await fetchProjectReports(selectedProject.id);
                } else {
                  await fetchComparisonReports();
                }
              }}
              onDocumentsChanged={async () => {
                if (currentNavigationView === 'project-details' && selectedProject?.id) {
                  refreshDocuments();
                } else if (selectedCustomer) {
                  await fetchCustomerDocuments(selectedCustomer.id);
                }
              }}
              onNextChatPage={handleNextChatPage}
              onPrevChatPage={handlePrevChatPage}
              onNextReportsPage={handleNextReportsPage}
              onPrevReportsPage={handlePrevReportsPage}
              onNextDocumentsPage={handleNextDocumentsPage}
              onPrevDocumentsPage={handlePrevDocumentsPage}
              DOCUMENTS_PER_PAGE={DOCUMENTS_PER_PAGE}
              autoOpenInsuranceSummaryId={pendingInsuranceSummaryId}
              onAutoOpenConsumed={() => setPendingInsuranceSummaryId(null)}
              onShowActivitySummary={() => {
                if (selectedProject) {
                  setActivityProjectId(selectedProject.id);
                  setActivityProjectTitle(selectedProject.title);
                }
              }}
            />
          )}
        </div>
      </div>

      <div className="flex flex-col gap-6">
        {/* Chat Interface section has been removed - chat is now only available on WorkflowReport pages */}
                </div>
      {/* Contact Information Overlay */}
      {showContactOverlay && selectedCustomer && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setShowContactOverlay(false)}
        >
          <div 
            className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl shadow-xl max-w-md w-full mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-secondary dark:text-dark-text flex items-center gap-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-primary">
                  <path d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z" />
                </svg>
                Contact Information
              </h3>
                  <button
                onClick={() => setShowContactOverlay(false)}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg transition-colors"
                  >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                </svg>
                  </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <label className="text-sm font-medium text-accent dark:text-dark-muted">Name</label>
                <p className="text-lg font-medium text-secondary dark:text-dark-text">{selectedCustomer.name}</p>
              </div>
              
              {selectedCustomer.company && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <label className="text-sm font-medium text-accent dark:text-dark-muted">Company</label>
                  <p className="text-lg font-medium text-secondary dark:text-dark-text">{selectedCustomer.company}</p>
                </div>
              )}
              
              {selectedCustomer.email && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <label className="text-sm font-medium text-accent dark:text-dark-muted">Email</label>
                  <p className="text-lg font-medium text-secondary dark:text-dark-text">{selectedCustomer.email}</p>
                </div>
              )}
              
              {selectedCustomer.phone && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <label className="text-sm font-medium text-accent dark:text-dark-muted">Phone</label>
                  <p className="text-lg font-medium text-secondary dark:text-dark-text">{selectedCustomer.phone}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <label className="text-sm font-medium text-accent dark:text-dark-muted">Status</label>
                  <p className="text-lg font-medium text-secondary dark:text-dark-text capitalize">{selectedCustomer.status}</p>
                </div>
                
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <label className="text-sm font-medium text-accent dark:text-dark-muted">Type</label>
                  <p className="text-lg font-medium text-secondary dark:text-dark-text capitalize">{selectedCustomer.type}</p>
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <label className="text-sm font-medium text-accent dark:text-dark-muted">Created</label>
                <p className="text-lg font-medium text-secondary dark:text-dark-text">
                  {new Date(selectedCustomer.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
              
              {selectedCustomer.lastContact && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <label className="text-sm font-medium text-accent dark:text-dark-muted">Last Contact</label>
                  <p className="text-lg font-medium text-secondary dark:text-dark-text">
                    {new Date(selectedCustomer.lastContact).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end mt-6">
                  <button
                onClick={() => setShowContactOverlay(false)}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                Close
                  </button>
                </div>
            </div>
          </div>
        )}

        {/* Document Upload Modal */}
      {showUploader && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-surface rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-[#0e161b] dark:text-dark-text">Upload Documents for {selectedCustomer?.name || 'Customer'}</h3>
              <button
                onClick={() => setShowUploader(false)}
                className="text-[#4e7a97] dark:text-dark-muted hover:text-[#0e161b] dark:hover:text-dark-text transition-colors"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                      </svg>
              </button>
            </div>
            
            {/* customerId/projectId are required: the backend rejects uploads
                without a projectId (400 PROJECT_ID_REQUIRED). Drag-and-drop
                passes them via useProjectFileDrop; this modal must too. */}
            <FileUploader
              onFilesUploaded={handleFilesUploaded}
              customerId={selectedCustomer?.id}
              projectId={selectedProject?.id}
            />
                    </div>
                  </div>
                )}

      {/* Document Viewer Overlay */}
      {showDocumentViewer && viewingDocument && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setShowDocumentViewer(false)}
        >
          <div 
            className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl shadow-xl w-full h-full max-w-6xl max-h-[90vh] mx-4 my-4 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-dark-border">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-green-600 dark:text-green-400">
                  <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                </svg>
              </div>
                <div>
                  <h3 className="text-lg font-semibold text-secondary dark:text-dark-text">
                    {viewingDocument.name}
              </h3>
                  <div className="flex items-center gap-2 text-sm text-accent dark:text-dark-muted mt-1">
                    <DocumentIntelligenceChip documentId={viewingDocument.id} />
                    <span>{(viewingDocument.size / (1024 * 1024)).toFixed(2)} MB</span>
                  </div>
                </div>
              </div>
                <button
                onClick={() => setShowDocumentViewer(false)}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                </svg>
                  </button>
                </div>
            
            {/* Document Content */}
            <div className="flex-1 p-4 overflow-hidden">
              {(() => {
                // Determine the best way to display the document
                const hasFileUrl = viewingDocument.fileUrl;
                const hasLocalFile = viewingDocument.file;
                const hasExtractedText = viewingDocument.extractedText;
                const isPdf = viewingDocument.type?.includes('pdf') || viewingDocument.name?.toLowerCase().endsWith('.pdf');
                
                devLog('📄 Document viewer state:', {
                  name: viewingDocument.name,
                  type: viewingDocument.type,
                  isPdf,
                  hasFileUrl,
                  hasLocalFile,
                  hasExtractedText,
                  fileUrl: viewingDocument.fileUrl?.substring(0, 100) + '...'
                });

                // Use PDF viewer for PDF files
                if (isPdf && (hasFileUrl || hasLocalFile)) {
                  // If we have a direct fileUrl (like base64 data), use PdfViewer directly
                  if (hasFileUrl && viewingDocument.fileUrl) {
                    devLog('📄 Using direct PdfViewer with fileUrl:', viewingDocument.fileUrl.startsWith('data:') ? 'base64 data' : 'other URL');
                    return (
                      <PdfViewer
                        fileUrl={viewingDocument.fileUrl}
                        highlight={highlight || undefined}
                        onClose={() => setViewingDocument(null)}
                      />
                    );
                  }
                  // Otherwise use PdfDocumentViewer for document IDs
                  return (
                    <PdfDocumentViewer
                      documentId={viewingDocument.id}
                      citation={highlight ? {
                        documentId: viewingDocument.id,
                        documentName: viewingDocument.name,
                        page: highlight.page || 1,
                        startOffset: 0, // CitationHighlight doesn't have offsets
                        endOffset: 100, // Use placeholder values
                        text: 'Document highlight' // Placeholder text
                      } : undefined}
                      onClose={() => setViewingDocument(null)}
                    />
                  );
                }

                // Fallback for non-PDF files or when PDF viewer fails
                if (hasFileUrl || hasLocalFile) {
                  const src = hasFileUrl ? viewingDocument.fileUrl : (hasLocalFile && viewingDocument.file ? URL.createObjectURL(viewingDocument.file) : '');
                  return (
                    <div className="w-full h-full">
                      <iframe
                        src={src}
                        className="w-full h-full border border-gray-200 dark:border-gray-700 rounded-lg"
                        title={viewingDocument.name}
                        onLoad={() => {
                          devLog('📄 Iframe loaded successfully');
                        }}
                        onError={() => {
                          devWarn('📄 Iframe failed to load');
                        }}
                      />
                      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        If the document doesn't load, try the download button below.
            </div>
          </div>
                  );
                }

                // Final fallback - show extracted text and download option
                return (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center max-w-2xl">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400">
                          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-secondary dark:text-dark-text mb-2">
                        Document Preview Unavailable
              </h3>
                      <p className="text-accent dark:text-dark-muted mb-4">
                        This document cannot be previewed in the browser. You can download it or view the extracted content below.
                      </p>
                      
                      {/* Download Button */}
                      {viewingDocument.id && (
              <button
                          onClick={async () => {
                            try {
                              devLog('📄 Attempting to download document:', viewingDocument.id);
                              const response = await fetch(`${API_BASE_URL}/api/documents/${viewingDocument.id}/download`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              if (response.ok) {
                                const data = await response.json();
                                devLog('📄 Opening download URL:', data.downloadUrl);
                                window.open(data.downloadUrl, '_blank');
                              } else {
                                console.error('📄 Download failed:', response.status, await response.text());
                                alert('Failed to download document. Please try again.');
                              }
                            } catch (error) {
                              console.error('📄 Download error:', error);
                              alert('Error downloading document. Please check your connection.');
                            }
                          }}
                          className="mb-6 px-6 py-3 bg-primary text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
              >
                          📥 Download Document
              </button>
                      )}

                      {/* Extracted Text Content */}
                      {hasExtractedText && (
                        <div className="text-left">
                          <h4 className="text-md font-medium text-secondary dark:text-dark-text mb-3">Document Content:</h4>
                          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg text-sm text-secondary dark:text-dark-text max-h-96 overflow-y-auto border">
                            <pre className="whitespace-pre-wrap font-sans">
                              {viewingDocument.extractedText && viewingDocument.extractedText.length > 2000 
                                ? viewingDocument.extractedText.substring(0, 2000) + '\n\n... (content truncated, download full document to see more)'
                                : viewingDocument.extractedText || 'No content available'
                              }
                            </pre>
            </div>
                    </div>
                      )}

                      {/* Debug Info */}
                      <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-xs text-left">
                        <strong>Debug Info:</strong><br/>
                        File Type: {viewingDocument.type || 'Unknown'}<br/>
                        Is PDF: {isPdf ? 'Yes' : 'No'}<br/>
                        File URL: {viewingDocument.fileUrl ? 'Available' : 'Not available'}<br/>
                        Local File: {viewingDocument.file ? 'Available' : 'Not available'}<br/>
                        Extracted Text: {hasExtractedText && viewingDocument.extractedText ? `${viewingDocument.extractedText.length} characters` : 'Not available'}<br/>
                        Document ID: {viewingDocument.id || 'Not available'}
                    </div>
                  </div>
                </div>
                );
              })()}
            </div>
            
            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-dark-border">
              <div className="text-sm text-accent dark:text-dark-muted">
                Uploaded: {new Date(viewingDocument.uploadedAt).toLocaleDateString()}
          </div>
              <div className="flex gap-2">
                {(viewingDocument.fileUrl || viewingDocument.file) && (
                  <a
                    href={viewingDocument.fileUrl || (viewingDocument.file ? URL.createObjectURL(viewingDocument.file) : '#')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-secondary dark:text-dark-text text-sm rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    Open in New Tab
                  </a>
                )}
                <button
                  onClick={() => setShowDocumentViewer(false)}
                  className="px-3 py-2 bg-primary text-white text-sm rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
              </div>
            </div>
      )}

      {/* HTML PDF Viewer for Citations */}
      {showHtmlPdfViewer && htmlViewerCitation && htmlViewerDocumentUrl && (
        <HtmlPdfViewer
          documentUrl={htmlViewerDocumentUrl}
          citation={{
            documentId: htmlViewerCitation.documentId || '',
            documentName: htmlViewerCitation.documentName || 'Unknown Document',
            page: htmlViewerCitation.page || 1,
            startOffset: htmlViewerCitation.startOffset || 0,
            endOffset: htmlViewerCitation.endOffset || 0,
            text: htmlViewerCitation.text || '',
            context: htmlViewerCitation.context
          }}
          allCitations={allCitationsOnPage}
          onClose={() => {
            setShowHtmlPdfViewer(false);
            setHtmlViewerCitation(null);
            setHtmlViewerDocumentUrl('');
            setAllCitationsOnPage([]);
          }}
        />
      )}


      {/* New Customer/Prospect Modal */}
      <NewCustomerModal
        isOpen={showNewCustomerModal}
        onClose={() => setShowNewCustomerModal(false)}
        customerType={newCustomerType}
        onSubmit={handleSubmitCustomerForm}
        isCreating={isCreatingCustomer}
        form={newCustomerForm}
        setForm={setNewCustomerForm}
        includeAddress={includeAddress}
        setIncludeAddress={setIncludeAddress}
        address={customerAddress}
        setAddress={setCustomerAddress}
      />

      {/* Customer Chat (RAG) drawer */}
      <CustomerChatDrawer customer={chatCustomer} onClose={() => setChatCustomer(null)} />

      {/* Customer Edit/Delete Modals */}
      <EditCustomerModal
        customer={editingCustomer}
        isOpen={showEditCustomerModal}
        onClose={() => {
          setShowEditCustomerModal(false);
          setEditingCustomer(null);
        }}
        onSave={handleSaveCustomer}
      />

      <DeleteCustomerModal
        customer={editingCustomer}
        isOpen={showDeleteCustomerModal}
        onClose={() => {
          setShowDeleteCustomerModal(false);
          setEditingCustomer(null);
        }}
        onConfirm={handleConfirmDeleteCustomer}
      />

      {/* Group Management Modal */}
      <GroupManagementModal
        isOpen={showGroupModal}
        onClose={() => setShowGroupModal(false)}
      />

      {/* Customer Audit Log Modal */}
      <CustomerAuditModal
        isOpen={!!auditCustomerId}
        customerId={auditCustomerId}
        customerName={auditCustomerName}
        onClose={() => { setAuditCustomerId(null); setAuditCustomerName(''); }}
      />

      {/* Project Activity Summary Modal */}
      <ProjectActivityModal
        isOpen={!!activityProjectId}
        projectId={activityProjectId}
        projectTitle={activityProjectTitle}
        onClose={() => { setActivityProjectId(null); setActivityProjectTitle(''); }}
      />

      {/* Share Customer Dialog */}
      <ShareCustomerDialog
        isOpen={showShareDialog}
        onClose={() => {
          setShowShareDialog(false);
          setShareCustomerId('');
          setShareCustomerName('');
        }}
        customerId={shareCustomerId}
        customerName={shareCustomerName}
      />

      {/* Transfer Customer Dialog */}
      <TransferCustomerDialog
        isOpen={showTransferDialog}
        onClose={() => {
          setShowTransferDialog(false);
          setTransferCustomerId('');
          setTransferCustomerName('');
        }}
        onTransferComplete={() => {
          fetchCustomers();
        }}
        customerId={transferCustomerId}
        customerName={transferCustomerName}
      />

      {/* AMS360 Import Modal */}
      <AMS360ImportModal
        isOpen={showAMS360ImportModal}
        onClose={() => setShowAMS360ImportModal(false)}
        onImport={fetchCustomers}
      />
    </div>
  );
};

export default Home; 